/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useWBL } from '../../context/WBLContext';
import { Opportunity, SUBCLUSTERS_MAP } from '../../types';
import { StudentAvatar, isUrl, resolveGsUrl } from '../Common/StudentAvatar';
import { formatLastNameFirst, sortStudentsByLastName } from '../../utils/studentUtils';
import { doesRecordBelongToOrg, getCanonicalOrgTeamId } from '../../utils/orgTeamUtils';
import { ProfessionalOrgFallback } from '../Common/ProfessionalOrgFallback';
import { getRegionConstraints, RegionConstraint } from '../../utils/regionUtils';
import { VerifiedAddressInput } from '../Common/VerifiedAddressInput';
import landingHeroBg from '../../assets/images/landing_hero_bg_1780836416239.png';
import { HeroBanner } from '../Common/HeroBanner';
import { FerpaConsentReminderCard } from '../Common/FerpaConsentReminderCard';
import { SearchBar } from '../Common/SearchBar';
import { OpportunityMap } from '../Common/OpportunityMap';
import { db } from '../../firebase';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import { 
  Briefcase, Send, Plus, Search, MapPin, Clock, Calendar, CheckCircle, XCircle,
  Tag, Layers, ArrowLeft, ShieldAlert, Sparkles, HelpCircle, User, 
  DollarSign, CheckSquare, Square, Trash2, Eye, EyeOff, Globe,
  ArrowRight, GraduationCap, Activity, Cpu, Bold, Italic, Underline, 
  List, ListOrdered, ChevronDown, ChevronUp, Users, Info, Filter,
  Building, Upload, X, Link, Check, RefreshCw, RotateCcw, Image as ImageIcon,
  Lock, Mail, UserCheck, Loader2
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { CAREER_CLUSTERS } from '../../types';
import { ImageCropper } from './ImageCropper';
import { RichTextEditor, ensureHtml } from '../Common/RichTextEditor';
import { useInAppDialog } from '../Common/InAppDialog';

// Helper to compress and downscale data URLs before sending to Firestore to avoid the 1MB document limit
const compressImageIfDataUrl = async (dataUrl: string, maxDim = 1200, quality = 0.82): Promise<string> => {
  if (!dataUrl || !dataUrl.startsWith('data:image')) {
    return dataUrl;
  }
  // If string length is already tiny (< 100KB), no need to compress further
  if (dataUrl.length < 100000) {
    return dataUrl;
  }
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      let targetWidth = img.width;
      let targetHeight = img.height;
      if (targetWidth > maxDim) {
        const scale = maxDim / targetWidth;
        targetWidth = maxDim;
        targetHeight = Math.round(targetHeight * scale);
      }
      if (targetHeight > maxDim) {
        const scale = maxDim / targetHeight;
        targetHeight = maxDim;
        targetWidth = Math.round(targetWidth * scale);
      }
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, targetWidth);
      canvas.height = Math.max(1, targetHeight);
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      try {
        const compressed = canvas.toDataURL('image/jpeg', quality);
        resolve(compressed);
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
};

export const SYSTEM_METADATA_PRESETS = [
  { label: 'Education & Training', category: 'Education', url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=600' },
  { label: 'Health & Clinical Services', category: 'Health & Human Services', url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=600' },
  { label: 'Digital Tech & Software', category: 'Digital Technology', url: 'https://images.unsplash.com/photo-1605810230434-7631ac76ec81?auto=format&fit=crop&q=80&w=600' },
  { label: 'Business & Management', category: 'Business & Management', url: 'https://images.unsplash.com/photo-1521791136368-1a468d7de33f?auto=format&fit=crop&q=80&w=600' },
  { label: 'Manufacturing & Industrial', category: 'Manufacturing & Engineering', url: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80&w=600' },
  { label: 'Construction & Architecture', category: 'Construction', url: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&q=80&w=600' },
  { label: 'Creative Media & Arts', category: 'Arts & Communication', url: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=600' },
  { label: 'Culinary & Hospitality', category: 'Hospitality', url: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&q=80&w=600' },
];

const getOpportunityImage = (
  cluster?: string, 
  title?: string, 
  subcluster?: string, 
  globalBranding?: any, 
  oppId?: string,
  customImageUrl?: string
) => {
  if (customImageUrl && customImageUrl.trim() !== '') {
    return customImageUrl.trim();
  }
  if (subcluster && subcluster !== 'General' && globalBranding?.subclusterImages?.[subcluster]) {
    const urls = globalBranding.subclusterImages[subcluster].filter((url: string) => url);
    if (urls.length > 0) {
      // Deterministically select an image based on opportunity ID
      let index = 0;
      if (oppId) {
        let hash = 0;
        for (let i = 0; i < oppId.length; i++) {
          hash = oppId.charCodeAt(i) + ((hash << 5) - hash);
        }
        index = Math.abs(hash) % urls.length;
      }
      return urls[index];
    }
  }

  if (cluster && globalBranding?.clusterImages?.[cluster]) {
    const urls = globalBranding.clusterImages[cluster].filter((url: string) => url);
    if (urls.length > 0) {
      let index = 0;
      if (oppId) {
        let hash = 0;
        for (let i = 0; i < oppId.length; i++) {
          hash = oppId.charCodeAt(i) + ((hash << 5) - hash);
        }
        index = Math.abs(hash) % urls.length;
      }
      return urls[index];
    }
  }

  const normCluster = (cluster || '').toLowerCase();
  const normTitle = (title || '').toLowerCase();

  if (normTitle.includes('educat') || normCluster.includes('educat')) {
    return 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80&w=600';
  }
  if (normTitle.includes('health') || normTitle.includes('clinic') || normTitle.includes('medic') || normCluster.includes('health')) {
    return 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80&w=600';
  }
  if (normTitle.includes('tech') || normTitle.includes('software') || normTitle.includes('dev') || normTitle.includes('cloud') || normTitle.includes('it ') || normCluster.includes('tech')) {
    return 'https://images.unsplash.com/photo-1605810230434-7631ac76ec81?auto=format&fit=crop&q=80&w=600';
  }
  return 'https://images.unsplash.com/photo-1521791136368-1a468d7de33f?auto=format&fit=crop&q=80&w=600';
};

const getClusterIcon = (cluster?: string) => {
  const norm = (cluster || '').toLowerCase();
  if (norm.includes('educat')) return <GraduationCap className="w-5 h-5 text-[#0094c8] animate-pulse" />;
  if (norm.includes('health')) return <Activity className="w-5 h-5 text-rose-600 animate-pulse" />;
  if (norm.includes('tech') || norm.includes('dev')) return <Cpu className="w-5 h-5 text-teal-600 animate-pulse" />;
  return <Briefcase className="w-5 h-5 text-amber-600 animate-pulse" />;
};

const resolveOpportunityCategories = (opp: Opportunity) => {
  let cluster = opp.careerCluster || opp.industry;
  let subcluster = opp.subcluster || opp.jobFunction;

  if (cluster === 'Information Technology' || cluster === 'Digital Technology' || cluster === 'IT') {
    cluster = 'Digital Technology';
  } else if (cluster === 'Health Science' || cluster === 'Healthcare') {
    cluster = 'Health & Human Services';
  }

  if (!cluster || !subcluster) {
    const title = (opp.title || '').toLowerCase();
    const desc = (opp.description || '').toLowerCase();

    if (title.includes('educat') || title.includes('child') || desc.includes('educat') || desc.includes('child') || desc.includes('school')) {
      cluster = 'Education';
      subcluster = 'Early Childhood Development';
    } else if (title.includes('health') || title.includes('clinic') || title.includes('medic') || title.includes('nurs') || title.includes('hospital') || desc.includes('health') || desc.includes('clinic') || desc.includes('medic') || desc.includes('nurs')) {
      cluster = 'Health & Human Services';
      subcluster = subcluster || 'Physical Health';
    } else if (title.includes('cloud') || title.includes('devops') || title.includes('software') || title.includes('react') || title.includes('frontend') || title.includes('web') || title.includes('it ') || title.includes('tech') || desc.includes('cloud') || desc.includes('software') || desc.includes('network') || desc.includes('cyber')) {
      cluster = 'Digital Technology';
      subcluster = subcluster || 'Web & Cloud';
    } else if (title.includes('build') || title.includes('construct') || title.includes('architect') || desc.includes('construct') || desc.includes('carpentry')) {
      cluster = 'Construction';
      subcluster = subcluster || 'Skilled Trades';
    } else if (title.includes('robot') || title.includes('manufactur') || title.includes('engin') || desc.includes('machinery')) {
      cluster = 'Advanced Manufacturing';
      subcluster = subcluster || 'Engineering';
    } else if (title.includes('farm') || title.includes('plant') || title.includes('agricult') || desc.includes('crop') || desc.includes('soil')) {
      cluster = 'Agriculture';
      subcluster = subcluster || 'Agribusiness';
    } else if (title.includes('design') || title.includes('art') || title.includes('media') || title.includes('music') || desc.includes('digital art') || desc.includes('video')) {
      cluster = 'Art, Entertainment, & Design';
      subcluster = subcluster || 'Design & Digital Arts';
    } else if (title.includes('clean energy') || title.includes('solar') || title.includes('ecosystem') || desc.includes('green energy')) {
      cluster = 'Energy & Natural Resources';
      subcluster = subcluster || 'Clean & Alternative Energy';
    } else if (title.includes('bank') || title.includes('finance') || title.includes('account') || desc.includes('invest')) {
      cluster = 'Financial Services';
      subcluster = subcluster || 'Accounting';
    } else if (title.includes('culinary') || title.includes('hotel') || title.includes('cook') || title.includes('event') || desc.includes('food services')) {
      cluster = 'Hospitality, Events, & Tourism';
      subcluster = subcluster || 'Culinary & Food Services';
    } else {
      cluster = cluster || 'Education';
      subcluster = subcluster || 'Early Childhood Development';
    }
  }

  return {
    careerCluster: cluster,
    subcluster: subcluster
  };
};

const getOpportunityTimestamp = (dateVal: any): number => {
  if (!dateVal) return 0;
  try {
    if (typeof dateVal === 'number') return dateVal;
    if (typeof dateVal === 'string') {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    }
    if (dateVal && typeof dateVal === 'object') {
      if (typeof dateVal.toDate === 'function') {
        return dateVal.toDate().getTime();
      }
      if (typeof dateVal.seconds === 'number') {
        return dateVal.seconds * 1000;
      }
      if (typeof dateVal.getTime === 'function') {
        return dateVal.getTime();
      }
    }
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  } catch (e) {
    return 0;
  }
};

const formatOpportunityDate = (dateVal: any) => {
  if (!dateVal) return 'Active Session';
  try {
    if (typeof dateVal === 'string') {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? 'Active Session' : d.toLocaleDateString();
    }
    if (dateVal && typeof dateVal === 'object') {
      if (typeof dateVal.toDate === 'function') {
        return dateVal.toDate().toLocaleDateString();
      }
      if (typeof dateVal.seconds === 'number') {
        return new Date(dateVal.seconds * 1000).toLocaleDateString();
      }
    }
    const d = new Date(dateVal);
    return isNaN(d.getTime()) ? 'Active Session' : d.toLocaleDateString();
  } catch (e) {
    return 'Active Session';
  }
};

const renderRichContent = (htmlContent?: string) => {
  if (!htmlContent) return <p className="text-slate-400 italic">Not specified</p>;
  
  const processedContent = ensureHtml(htmlContent);

  return (
    <div 
      className="text-slate-700 leading-relaxed text-xs md:text-[13px] prose prose-slate max-w-none space-y-2 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_strong]:font-bold [&_u]:underline h-auto font-normal [&_a]:text-[#0094c8] [&_a]:underline hover:[&_a]:text-[#0077a3] [&_a]:font-medium [&_a]:cursor-pointer"
      dangerouslySetInnerHTML={{ __html: processedContent }} 
    />
  );
};

const getCleanExcerpt = (htmlOrText?: string) => {
  if (!htmlOrText) return '';
  
  let textToExtract = htmlOrText;
  
  // Find index of Responsibilities, Key Responsibilities, Requirements etc.
  const lowercaseText = textToExtract.toLowerCase();
  const markers = [
    '**responsibilities:**',
    'responsibilities:',
    '**key responsibilities:**',
    'key responsibilities:',
    '**duties:**',
    'duties:',
    '**requirements:**',
    'requirements:',
    '**qualifications:**',
    'qualifications:'
  ];
  
  let earliestIdx = -1;
  for (const marker of markers) {
    const idx = lowercaseText.indexOf(marker);
    if (idx !== -1) {
      if (earliestIdx === -1 || idx < earliestIdx) {
        earliestIdx = idx;
      }
    }
  }
  
  if (earliestIdx !== -1) {
    textToExtract = textToExtract.substring(0, earliestIdx);
  }
  
  // Now remove any variation of **Overview:** or Overview:
  textToExtract = textToExtract
    .replace(/\*\*overview:\*\*/gi, '')
    .replace(/overview:/gi, '')
    .trim();
  
  // Strip HTML
  const clean = textToExtract.replace(/<\/?[^>]+(>|$)/g, " ");
  
  // Remove markdown bold remnants
  const finalClean = clean.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  
  return finalClean;
};

const getOpportunitySections = (opp: any) => {
  const desc = opp.description || '';
  
  // Try to find Overview part
  let overview = '';
  let responsibilities = '';
  
  if (desc.includes('**Overview:**') || desc.includes('Overview:')) {
    // Split on Responsibilities header (either bold markdown or clean text)
    const resIdx = desc.indexOf('**Responsibilities:**') !== -1 
      ? desc.indexOf('**Responsibilities:**') 
      : desc.indexOf('Responsibilities:');
      
    if (resIdx !== -1) {
      overview = desc.substring(0, resIdx)
        .replace('**Overview:**', '')
        .replace('Overview:', '')
        .trim();
        
      responsibilities = desc.substring(resIdx)
        .replace('**Responsibilities:**', '')
        .replace('Responsibilities:', '')
        .trim();
    } else {
      overview = desc.replace('**Overview:**', '').replace('Overview:', '').trim();
    }
  } else {
    overview = desc;
  }

  // If Key Responsibilities are explicitly set on the object, use that
  if (opp.keyResponsibilities) {
    responsibilities = opp.keyResponsibilities;
  }
  
  // Set default responsibilities if blank
  if (!responsibilities.trim()) {
    responsibilities = 'Participate in assigned daily organizational tasks, assist staff on specific work assignments, and practice core pathway career competencies.';
  }

  // Qualifications
  let qualifications = '';
  if (opp.qualifications) {
    qualifications = opp.qualifications;
  } else if (opp.requirements && opp.requirements.length > 0) {
    qualifications = opp.requirements.join('\n');
  } else {
    qualifications = 'High school level student in good academic standing, aligned career goals, and strong interest in learning WBL competencies.';
  }

  // Focus Skillsets
  let focusSkills = '';
  if (opp.skills) {
    focusSkills = opp.skills;
  } else {
    const sub = opp.subcluster || opp.jobFunction || '';
    const title = opp.title || '';
    const cluster = opp.careerCluster || '';
    const skillsList: string[] = [];
    
    if (sub) skillsList.push(sub);
    
    if (cluster.toLowerCase().includes('education') || title.toLowerCase().includes('children') || title.toLowerCase().includes('education') || sub.toLowerCase().includes('childhood')) {
      skillsList.push('Early Childhood Development', 'Childhood Pedagogies', 'Active Supervision', 'Classroom Observation');
    } else if (cluster.toLowerCase().includes('information') || title.toLowerCase().includes('tech') || title.toLowerCase().includes('developer') || title.toLowerCase().includes('engineer')) {
      skillsList.push('Technical Communication', 'Version Control / Git', 'Agile Workflows', 'Problem Solving');
    } else if (cluster.toLowerCase().includes('health') || title.toLowerCase().includes('medical') || title.toLowerCase().includes('nursing')) {
      skillsList.push('Interpersonal Communication', 'Healthcare Ethics', 'Clinical Safety', 'Patient Interaction');
    } else {
      skillsList.push('Professional Communication', 'Collaboration & Teamwork', 'Safety Protocols', 'Task Operations');
    }
    
    focusSkills = Array.from(new Set(skillsList)).join(', ');
  }

  return {
    overview,
    responsibilities,
    qualifications,
    skills: focusSkills
  };
};

const getOrganizationLogo = (orgName: string, orgLogo?: string, orgId?: string, sector?: string) => {
  const hasLogo = orgLogo && isUrl(orgLogo);
  if (!hasLogo) {
    return (
      <ProfessionalOrgFallback 
        orgName={orgName} 
        orgId={orgId} 
        variant="square" 
        className="w-full h-full" 
        sector={sector}
      />
    );
  }
  return (
    <div className="w-full h-full flex items-center justify-center overflow-hidden rounded-2xl bg-transparent">
      <StudentAvatar 
        studentName={orgName}
        studentId={orgId}
        imageUrl={orgLogo}
        className="w-full h-full object-contain bg-transparent"
      />
    </div>
  );
};

const getFallbackDistrictId = (schoolName: string, regionId: string): string => {
  const normSchool = (schoolName || '').toLowerCase();
  const normRegion = (regionId || '').toLowerCase();
  if (normRegion === 'training') {
    return 'Training School District (training)';
  }
  if (normRegion === 'charleston') {
    return 'Charleston County School District (charleston)';
  }
  if (normRegion === 'greenville') {
    return 'Greenville County School District (greenville)';
  }
  if (normRegion === 'horry') {
    return 'Horry County Schools (horry)';
  }
  if (normRegion === 'richland') {
    if (normSchool.includes('columbia') || normSchool.includes('dreher')) {
      return 'Richland School District One (richland)';
    }
    return 'Richland School District Two (richland)';
  }
  if (normRegion === 'spartanburg') {
    if (normSchool.includes('chapman') || normSchool.includes('landrum') || normSchool.includes('campobello')) {
      return 'Spartanburg School District 1 (spartanburg)';
    }
    if (normSchool.includes('boiling') || normSchool.includes('chesnee')) {
      return 'Spartanburg School District 2 (spartanburg)';
    }
    if (normSchool.includes('broome')) {
      return 'Spartanburg School District 3 (spartanburg)';
    }
    if (normSchool.includes('woodruff')) {
      return 'Spartanburg School District 4 (spartanburg)';
    }
    if (normSchool.includes('byrnes')) {
      return 'Spartanburg School District 5 (spartanburg)';
    }
    if (normSchool.includes('dorman')) {
      return 'Spartanburg School District 6 (spartanburg)';
    }
    return 'Spartanburg School District 7 (spartanburg)';
  }
  return '';
};

const findDistrictForSchool = (schoolName: string, regionId: string, districts: any[]): string => {
  const normSchool = (schoolName || '').toLowerCase();
  const normRegion = (regionId || '').toLowerCase();

  // Filter districts of this region
  const regionDistricts = districts.filter(d => d.regionId === normRegion);
  if (regionDistricts.length === 1) {
    return regionDistricts[0].id;
  }

  // If there are multiple districts in the region (like Spartanburg or Richland)
  if (normRegion === 'richland') {
    const isOne = normSchool.includes('columbia') || normSchool.includes('dreher');
    const matched = regionDistricts.find(d => (d.name || '').toLowerCase().includes(isOne ? 'one' : 'two'));
    if (matched) return matched.id;
  }

  if (normRegion === 'spartanburg') {
    let num = '';
    if (normSchool.includes('chapman') || normSchool.includes('landrum') || normSchool.includes('campobello')) {
      num = '1';
    } else if (normSchool.includes('boiling') || normSchool.includes('chesnee')) {
      num = '2';
    } else if (normSchool.includes('broome')) {
      num = '3';
    } else if (normSchool.includes('woodruff')) {
      num = '4';
    } else if (normSchool.includes('byrnes')) {
      num = '5';
    } else if (normSchool.includes('dorman')) {
      num = '6';
    } else {
      num = '7';
    }
    const matched = regionDistricts.find(d => (d.name || '').toLowerCase().endsWith(`district ${num} (spartanburg)`) || (d.name || '').toLowerCase().includes(`district ${num}`));
    if (matched) return matched.id;
  }

  // Fallback to first district in the region if any
  if (regionDistricts.length > 0) {
    return regionDistricts[0].id;
  }

  // Normalize fallback ID if there's no matching in DB yet
  const fallbackName = getFallbackDistrictId(schoolName, regionId);
  return fallbackName.toLowerCase().replace(/ /g, '-');
};

const getStudentEligibility = (
  opp: Opportunity,
  activeUser: any,
  dbDistricts: any[],
  dbSchools: any[]
): { eligible: boolean; reasons: string[] } => {
  if (!activeUser || activeUser.role !== 'student') {
    return { eligible: true, reasons: [] };
  }

  const reasons: string[] = [];

  // 1. District Check
  if (opp.targetSchoolDistricts && opp.targetSchoolDistricts.length > 0) {
    const studentDistrictIdsAndNames = new Set<string>();
    
    // Add primary district name if present
    if (activeUser.district) {
      studentDistrictIdsAndNames.add(activeUser.district.toLowerCase().trim());
    }
    // Add primary district ID if present
    if (activeUser.districtId) {
      studentDistrictIdsAndNames.add(activeUser.districtId.toLowerCase().trim());
    }
    
    // Look up primary school in DB to find its district ID/name
    const primarySchoolId = activeUser.schoolId;
    const primarySchoolName = activeUser.school;
    const primaryDbSchool = dbSchools.find(s => (primarySchoolId && s.id === primarySchoolId) || (primarySchoolName && s.name.toLowerCase().trim() === primarySchoolName.toLowerCase().trim()));
    if (primaryDbSchool && primaryDbSchool.districtId) {
      studentDistrictIdsAndNames.add(primaryDbSchool.districtId.toLowerCase().trim());
      const dist = dbDistricts.find(d => d.id === primaryDbSchool.districtId);
      if (dist) {
        studentDistrictIdsAndNames.add(dist.name.toLowerCase().trim());
        studentDistrictIdsAndNames.add(dist.id.toLowerCase().trim());
      }
    }

    // Look up additional schools to find their district IDs/names
    const additionalSchoolIds = activeUser.additionalSchoolIds || [];
    const additionalSchoolNames = activeUser.additionalSchools || [];
    
    additionalSchoolIds.forEach(id => {
      const sch = dbSchools.find(s => s.id === id);
      if (sch && sch.districtId) {
        studentDistrictIdsAndNames.add(sch.districtId.toLowerCase().trim());
        const dist = dbDistricts.find(d => d.id === sch.districtId);
        if (dist) {
          studentDistrictIdsAndNames.add(dist.name.toLowerCase().trim());
          studentDistrictIdsAndNames.add(dist.id.toLowerCase().trim());
        }
      }
    });

    additionalSchoolNames.forEach(name => {
      const sch = dbSchools.find(s => s.name.toLowerCase().trim() === name.toLowerCase().trim());
      if (sch && sch.districtId) {
        studentDistrictIdsAndNames.add(sch.districtId.toLowerCase().trim());
        const dist = dbDistricts.find(d => d.id === sch.districtId);
        if (dist) {
          studentDistrictIdsAndNames.add(dist.name.toLowerCase().trim());
          studentDistrictIdsAndNames.add(dist.id.toLowerCase().trim());
        }
      }
    });

    const hasMatch = opp.targetSchoolDistricts.some(id => {
      const idNorm = id.toLowerCase().trim();
      return studentDistrictIdsAndNames.has(idNorm);
    });

    if (!hasMatch) {
      reasons.push("This opportunity is restricted to other school districts.");
    }
  }

  // 2. School Check
  if (opp.targetSchools && opp.targetSchools.length > 0) {
    if (!activeUser.school && (!activeUser.additionalSchools || activeUser.additionalSchools.length === 0)) {
      reasons.push("Your profile must have a school specified.");
    } else {
      const studentSchoolId = activeUser.schoolId;
      const studentSchoolName = activeUser.school;
      const additionalSchoolIds = activeUser.additionalSchoolIds || [];
      const additionalSchoolNames = activeUser.additionalSchools || [];

      const hasSchoolMatch = opp.targetSchools.some(targetId => {
        // Match primary school ID
        if (studentSchoolId && targetId === studentSchoolId) return true;
        
        // Match primary school name
        if (studentSchoolName && targetId.toLowerCase().trim() === studentSchoolName.toLowerCase().trim()) return true;

        // Match additional school IDs
        if (additionalSchoolIds.includes(targetId)) return true;

        // Match additional school names
        if (additionalSchoolNames.some((name: string) => targetId.toLowerCase().trim() === name.toLowerCase().trim())) return true;
        
        // Match using DB objects
        const dbSchool = dbSchools.find(s => s.id === targetId || s.name === targetId);
        if (dbSchool) {
          if (studentSchoolId && dbSchool.id === studentSchoolId) return true;
          if (studentSchoolName && dbSchool.name.toLowerCase().trim() === studentSchoolName.toLowerCase().trim()) return true;
          if (additionalSchoolIds.includes(dbSchool.id)) return true;
          if (additionalSchoolNames.some((name: string) => dbSchool.name.toLowerCase().trim() === name.toLowerCase().trim())) return true;
        }
        return false;
      });

      if (!hasSchoolMatch) {
        reasons.push("This opportunity is restricted to other schools.");
      }
    }
  }

  // 3. Age Check
  if (opp.minAge !== undefined && opp.minAge !== null) {
    const dob = activeUser.dateOfBirth;
    if (!dob) {
      reasons.push(`Minimum age required: ${opp.minAge} (Please complete your Date of Birth in your profile).`);
    } else {
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age < opp.minAge) {
        reasons.push(`Minimum age required: ${opp.minAge} (Your current age is ${age}).`);
      }
    }
  }

  if (opp.maxAge !== undefined && opp.maxAge !== null) {
    const dob = activeUser.dateOfBirth;
    if (dob) {
      const today = new Date();
      const birthDate = new Date(dob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      if (age > opp.maxAge) {
        reasons.push(`Maximum age allowed: ${opp.maxAge} (Your current age is ${age}).`);
      }
    }
  }

  return {
    eligible: reasons.length === 0,
    reasons
  };
};

/**
 * Helper to determine if an opportunity is restricted to schools that do NOT include
 * any of the schools assigned to/served by this advisor.
 */
export const isOppRestrictedForOtherSchools = (
  opp: Opportunity,
  advisorUser: any,
  dbDistricts: any[],
  dbSchools: any[],
  allSchools: any[]
): boolean => {
  if (!advisorUser || advisorUser.role !== 'advisor') return false;

  const hasTargetSchools = Array.isArray(opp.targetSchools) && opp.targetSchools.length > 0;
  const hasTargetDistricts = Array.isArray(opp.targetSchoolDistricts) && opp.targetSchoolDistricts.length > 0;

  // If not targeted to any specific school or district, it's open to all
  if (!hasTargetSchools && !hasTargetDistricts) {
    return false;
  }

  // Collect all school references the advisor is associated with
  const advisorSchoolRefs = Array.from(new Set([
    advisorUser.schoolId,
    ...(advisorUser.schoolsServedIds || []),
    advisorUser.school,
    ...(advisorUser.schoolsServed || [])
  ].filter(Boolean) as string[]));

  if (advisorSchoolRefs.length === 0) {
    // Advisor has no assigned schools; any restricted opportunity is not for their school
    return true;
  }

  const pool = (dbSchools && dbSchools.length > 0 ? dbSchools : (allSchools || []));

  // Find all advisor schools in pool or construct representations
  const advisorSchoolsList: Array<{ id: string; name: string; districtId?: string; district?: string }> = [];

  advisorSchoolRefs.forEach(ref => {
    const normRef = ref.toLowerCase().trim().replace(/\s*\(.*\)$/, '');
    const matched = pool.filter((s: any) => {
      const normSchoolName = s.name ? s.name.toLowerCase().trim().replace(/\s*\(.*\)$/, '') : '';
      const normSchoolId = s.id ? s.id.toLowerCase().trim().replace(/\s*\(.*\)$/, '') : '';
      return normRef === normSchoolId || normRef === normSchoolName || ref === s.id || ref === s.name;
    });

    if (matched.length > 0) {
      matched.forEach((m: any) => {
        if (!advisorSchoolsList.some(as => as.id === m.id)) {
          advisorSchoolsList.push({
            id: m.id,
            name: m.name,
            districtId: m.districtId,
            district: m.district
          });
        }
      });
    } else {
      if (!advisorSchoolsList.some(as => as.id === ref || as.name.toLowerCase().trim() === normRef)) {
        advisorSchoolsList.push({
          id: ref,
          name: ref,
          districtId: advisorUser.districtId,
          district: advisorUser.district
        });
      }
    }
  });

  // Check if at least ONE of the advisor's schools satisfies the opportunity's restriction requirements
  const hasEligibleSchool = advisorSchoolsList.some(sch => {
    // School check
    if (hasTargetSchools) {
      const schIdNorm = sch.id.toLowerCase().trim();
      const schNameNorm = sch.name.toLowerCase().trim();
      const schMatches = opp.targetSchools!.some(targetId => {
        const targetNorm = (targetId || '').toLowerCase().trim();
        if (schIdNorm === targetNorm || schNameNorm === targetNorm) return true;
        const dbS = pool.find((s: any) => s.id === targetId || (s.name && s.name.toLowerCase().trim() === targetNorm));
        if (dbS && (dbS.id.toLowerCase().trim() === schIdNorm || (dbS.name && dbS.name.toLowerCase().trim() === schNameNorm))) {
          return true;
        }
        return false;
      });
      if (!schMatches) return false;
    }

    // District check
    if (hasTargetDistricts) {
      const distIdNorm = (sch.districtId || advisorUser.districtId || '').toLowerCase().trim();
      const distNameNorm = (sch.district || advisorUser.district || '').toLowerCase().trim();

      let dbDistrictObj: any = null;
      if (sch.districtId) {
        dbDistrictObj = (dbDistricts || []).find((d: any) => d.id === sch.districtId);
      }

      const distMatches = opp.targetSchoolDistricts!.some(targetDist => {
        const targetNorm = (targetDist || '').toLowerCase().trim();
        if (distIdNorm && distIdNorm === targetNorm) return true;
        if (distNameNorm && distNameNorm === targetNorm) return true;
        if (dbDistrictObj) {
          if (dbDistrictObj.id && dbDistrictObj.id.toLowerCase().trim() === targetNorm) return true;
          if (dbDistrictObj.name && dbDistrictObj.name.toLowerCase().trim() === targetNorm) return true;
        }
        const targetDbDist = (dbDistricts || []).find((d: any) => d.id === targetDist || (d.name && d.name.toLowerCase().trim() === targetNorm));
        if (targetDbDist) {
          if (distIdNorm && distIdNorm === targetDbDist.id.toLowerCase().trim()) return true;
          if (distNameNorm && distNameNorm === (targetDbDist.name || '').toLowerCase().trim()) return true;
        }
        return false;
      });
      if (!distMatches) return false;
    }

    return true;
  });

  // If at least one school can participate, it is NOT restricted for other schools
  return !hasEligibleSchool;
};

// Backwards compatibility alias
export const isOppGeoFencedForOtherSchools = isOppRestrictedForOtherSchools;

export const OpportunitiesPage: React.FC = () => {
  const {
    activeUser,
    activeTenant,
    opportunities,
    applications,
    tenants,
    users,
    addOpportunity,
    updateOpportunity,
    applyToOpportunity,
    recommendStudentToOpportunity,
    recommendations,
    userLinks,
    setCurrentView,
    globalBranding,
    selectedTerm,
    systemConfig,
    allSchools,
    sendVerificationReminderToAdvisor
  } = useWBL();

  const { showAlert, showConfirm, showToast } = useInAppDialog();

  const [sendingReminder, setSendingReminder] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);

  const handleSendAdvisorReminder = async () => {
    setSendingReminder(true);
    try {
      await sendVerificationReminderToAdvisor();
      setReminderSent(true);
      showToast({
        title: "Reminder Sent",
        message: "Verification reminder sent to your school advisor!",
        type: "success"
      });
    } catch (err: any) {
      showAlert({
        title: "Failed to Send Reminder",
        message: "Unable to send verification reminder: " + (err?.message || "Please retry later."),
        type: "error"
      });
    } finally {
      setSendingReminder(false);
    }
  };

  const [activeTab, setActiveTab] = useState<'all' | 'post' | 'details'>('all');
  const [selectedOpp, setSelectedOpp] = useState<Opportunity | null>(null);
  const detailPageRef = useRef<HTMLDivElement>(null);

  const resetScrollToTop = () => {
    const scrollableArea = document.getElementById('scrollable-content-area');
    if (scrollableArea) {
      scrollableArea.scrollTop = 0;
    }
    window.scrollTo(0, 0);
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
    }
    if (document.body) {
      document.body.scrollTop = 0;
    }
    detailPageRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  };

  useEffect(() => {
    if (activeTab === 'details') {
      resetScrollToTop();
      const raf = requestAnimationFrame(resetScrollToTop);
      const t1 = setTimeout(resetScrollToTop, 20);
      const t2 = setTimeout(resetScrollToTop, 80);
      const t3 = setTimeout(resetScrollToTop, 200);
      return () => {
        cancelAnimationFrame(raf);
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, [activeTab, selectedOpp?.id]);

  const handleViewOpportunity = (opp: Opportunity) => {
    resetScrollToTop();
    setSelectedOpp(opp);
    setActiveTab('details');
    requestAnimationFrame(resetScrollToTop);
  };

  const handleBackToOpportunities = () => {
    setActiveTab('all');
    setSelectedOpp(null);
    resetScrollToTop();
  };
  
  const [editingOppId, setEditingOppId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRegionsDropdown, setShowRegionsDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // New UI Filters
  const [showMyPostings, setShowMyPostings] = useState(false);
  const [showAppliedOnly, setShowAppliedOnly] = useState(false);
  const [showRestricted, setShowRestricted] = useState(false);
  const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);
  const [filterCluster, setFilterCluster] = useState<string>('all');
  const [filterFunction, setFilterFunction] = useState<string>('all');
  const [filterCompensation, setFilterCompensation] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('newest');
  const [showClosedRoles, setShowClosedRoles] = useState(false);

  // Submit states
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Application Modal state (for students applying)
  const [selectedOppForApply, setSelectedOppForApply] = useState<Opportunity | null>(null);
  const [coverLetter, setCoverLetter] = useState('');
  const [resumeSummary, setResumeSummary] = useState('');
  const [questionAnswers, setQuestionAnswers] = useState<Record<number, string>>({});

  useEffect(() => {
    setQuestionAnswers({});
  }, [selectedOppForApply?.id]);

  // Find user's applications
  const myApplications = useMemo(() => {
    if (!activeUser) return [];
    return (applications || []).filter(a => a.studentId === activeUser.uid);
  }, [applications, activeUser]);

  // Recommendation Modal state (for advisors recommending students)
  const [recommendModalOpen, setRecommendModalOpen] = useState(false);
  const [selectedStudentsForRecommend, setSelectedStudentsForRecommend] = useState<string[]>([]);
  const [recommendationProcessing, setRecommendationProcessing] = useState(false);
  const [recommendationComplete, setRecommendationComplete] = useState(false);
  const [recommendationProgress, setRecommendationProgress] = useState(0);

  // Parent Multi-Child Selection State
  const [selectedChildId, setSelectedChildId] = useState<string>('');

  const myChildren = useMemo(() => {
    if (!activeUser || activeUser.role !== 'parent') return [];
    
    const studentIds = new Set<string>();
    if (activeUser.childId) {
      studentIds.add(activeUser.childId);
    }
    
    (userLinks || []).forEach(link => {
      if (link.parentId === activeUser.uid && link.studentId) {
        studentIds.add(link.studentId);
      }
    });
    
    if (activeUser.email) {
      (users || []).forEach(u => {
        if (u.role === 'student' && u.parentEmail?.toLowerCase() === activeUser.email?.toLowerCase()) {
          studentIds.add(u.uid);
        }
      });
    }
    
    return Array.from(studentIds).map(sid => {
      const found = (users || []).find(u => u.uid === sid);
      return found || { uid: sid, name: 'Student' };
    }).filter(s => s && s.name);
  }, [activeUser, userLinks, users]);

  // Filter students for advisor recommendation selection
  const advisorAdvisees = useMemo(() => {
    if (!activeUser || activeUser.role !== 'advisor') return [];
    if (!activeUser.verified) return [];

    // Official Assignment: IDs provided by Admin
    const advisorSchoolIds = Array.from(new Set([
      activeUser.schoolId,
      ...(activeUser.schoolsServedIds || [])
    ].filter(Boolean) as string[]));

    if (advisorSchoolIds.length === 0) return [];

    return (users || []).filter(u => {
      if (u.role !== 'student') return false;
      if (!u.schoolId && !u.school) return false;

      // Verify student has a signed FERPA consent
      const ferpaGranted = typeof u.ferpaConsent === 'object'
        ? u.ferpaConsent?.granted === true
        : u.ferpaConsent === true;
      if (!ferpaGranted) return false;

      // Check 1: ID Match (Preferred)
      if (u.schoolId && advisorSchoolIds.includes(u.schoolId)) return true;

      // Check 2: Name Match (Only if the name resolves to one of the advisor's officially assigned school IDs)
      if (u.school) {
        const studentSchoolNorm = u.school.toLowerCase().trim();
        const studentSchool = (allSchools || []).find(s => {
          const normSchoolId = s.id.toLowerCase().trim();
          const normSchoolName = s.name.toLowerCase().trim();
          return studentSchoolNorm === normSchoolId || studentSchoolNorm === normSchoolName;
        });

        if (studentSchool && advisorSchoolIds.includes(studentSchool.id)) return true;
      }

      return false;
    });
  }, [users, allSchools, activeUser]);

  const handleRecommendPlacement = async () => {
    if (!selectedOpp || selectedStudentsForRecommend.length === 0) return;

    // Filter strictly to students who can apply
    const validStudentIds = selectedStudentsForRecommend.filter(studentId => {
      const student = (users || []).find(u => u.uid === studentId);
      if (!student) return false;
      return getStudentEligibility(selectedOpp, student, dbDistricts, dbSchools).eligible;
    });

    if (validStudentIds.length === 0) {
      showAlert({
        title: "Recommendation Ineligible",
        message: "None of the selected students are eligible to apply to this opportunity due to school, district, or age restrictions.",
        type: "error"
      });
      return;
    }
    
    setRecommendationProcessing(true);
    setRecommendationProgress(10);
    
    try {
      // Simulate progress UI
      const interval = setInterval(() => {
        setRecommendationProgress(prev => {
          if (prev >= 90) return 90;
          return prev + 5;
        });
      }, 200);

      // Perform real dispatching for each eligible student
      for (let i = 0; i < validStudentIds.length; i++) {
        const studentId = validStudentIds[i];
        await recommendStudentToOpportunity(studentId, selectedOpp.id);
        
        // Update progress based on completion percentage
        const percentPerStudent = 90 / validStudentIds.length;
        setRecommendationProgress(10 + (percentPerStudent * (i + 1)));
      }
      
      clearInterval(interval);
      setRecommendationProgress(100);
      setRecommendationComplete(true);
      
      // Reset after success
      setTimeout(() => {
        setRecommendModalOpen(false);
        setRecommendationComplete(false);
        setRecommendationProcessing(false);
        setRecommendationProgress(0);
        setSelectedStudentsForRecommend([]);
      }, 2500);

    } catch (error) {
      console.error("Recommendation failed:", error);
      setRecommendationProcessing(false);
    }
  };

  // ----------------------------------------------------
  // EXTENSIVE POST FORM STATE FIELDS BASED ON USER_REQUEST
  // ----------------------------------------------------
  const [isVisible, setIsVisible] = useState(true);
  const [isOpenState, setIsOpenState] = useState(true);
  
  // Regional routing - list of tenant IDs we replicate this posting to
  const [selectedRegions, setSelectedRegions] = useState<string[]>(activeTenant?.id ? [activeTenant.id] : []);

  // Filter regional routing based on granted access regions
  const allowedRegions = useMemo(() => {
    if (!activeUser) return [];
    if (activeUser.role === 'superadmin') {
      return (tenants || []);
    }
    // Match home tenantId and any authorized extended regions
    const grantedIds = new Set<string>();
    if (activeUser.tenantId) {
      grantedIds.add(activeUser.tenantId);
    }
    if (activeUser.extendedRegions) {
      (activeUser.extendedRegions || []).forEach(id => {
        if (id) grantedIds.add(id);
      });
    }
    const filtered = (tenants || []).filter(t => grantedIds.has(t.id));
    // Fallback: guarantee at least the active tenant is available
    if (filtered.length === 0) {
      const activeT = (tenants || []).find(t => t.id === activeTenant?.id);
      return activeT ? [activeT] : (tenants || []).slice(0, 1);
    }
    return filtered;
  }, [tenants, activeUser, activeTenant]);

  // General Placement Coordinates
  const [formTitle, setFormTitle] = useState('');
  const [careerCluster, setCareerCluster] = useState('');
  const [functionalFocus, setFunctionalFocus] = useState('');
  const [occupationSelection, setOccupationSelection] = useState('');
  const [worksiteAddress, setWorksiteAddress] = useState(activeUser?.address || activeTenant?.address || '');
  const [semester, setSemester] = useState(selectedTerm);
  const [registryDeadline, setRegistryDeadline] = useState('');
  const [targetLevel, setTargetLevel] = useState('High School');
  const [openingsCount, setOpeningsCount] = useState(1);
  const [totalHours, setTotalHours] = useState(0);

  // Smart Search States
  const [uploadedCareers, setUploadedCareers] = useState<{ id: string; cluster: string; subclusters: string[]; code: string; occupation: string; }[]>([]);
  const [loadingCareers, setLoadingCareers] = useState(false);
  const [smartSearchQuery, setSmartSearchQuery] = useState('');
  const [smartSearchCluster, setSmartSearchCluster] = useState('');
  const [smartSearchSubcluster, setSmartSearchSubcluster] = useState('');
  const [selectedOccupationForSetup, setSelectedOccupationForSetup] = useState<{
    occupation: string;
    cluster: string;
    subclusters: string[];
  } | null>(null);
  const [showAppliedAlert, setShowAppliedAlert] = useState(false);

  // Operational Supervisor
  const [supervisorName, setSupervisorName] = useState('');
  const [supervisorTitle, setSupervisorTitle] = useState('');

  // Compensation
  const [compensationType, setCompensationType] = useState<'paid' | 'unpaid'>('paid');
  const [payStructure, setPayStructure] = useState('Hourly Wage');
  const [payAmount, setPayAmount] = useState('');

  // Age Constraints
  const [minAge, setMinAge] = useState<string>('');
  const [maxAge, setMaxAge] = useState<string>('');

  const [dbDistricts, setDbDistricts] = useState<{ id: string; name: string; regionId: string }[]>([]);
  const [dbSchools, setDbSchools] = useState<{ id: string; name: string; regionId: string; districtId: string }[]>([]);

  useEffect(() => {
    // 1. Fetch districts and schools once on mount
    const fetchDistrictsAndSchools = async () => {
      try {
        const districtsSnap = await getDocs(collection(db, 'districts'));
        const schoolsSnap = await getDocs(collection(db, 'schools'));
        
        setDbDistricts(districtsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as { id: string; name: string; regionId: string })));
        setDbSchools(schoolsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() } as { id: string; name: string; regionId: string; districtId: string })));
      } catch (err) {
        console.error("Error fetching districts/schools:", err);
      }
    };
    fetchDistrictsAndSchools();

    // 2. Setup real-time onSnapshot listener for the careers collection
    setLoadingCareers(true);
    const unsubscribeCareers = onSnapshot(collection(db, 'careers'), (snap) => {
      const loaded: any[] = [];
      snap.forEach(doc => {
        const data = doc.data();
        loaded.push({
          id: doc.id,
          cluster: data.cluster || '',
          subclusters: Array.isArray(data.subclusters) ? data.subclusters : [],
          code: data.code || '',
          occupation: data.occupation || ''
        });
      });
      setUploadedCareers(loaded);
      setLoadingCareers(false);
    }, (err) => {
      console.error("Error watching careers collection in real-time:", err);
      setLoadingCareers(false);
    });

    return () => {
      unsubscribeCareers();
    };
  }, []);

  // Institutional Constraints
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [selectedInstitutions, setSelectedInstitutions] = useState<string[]>([]);

  // Compute available districts & schools dynamically based on selectedRegions
  const { availableDistricts, availableSchools: rawAvailableSchools, availableRegionSchools } = useMemo(() => {
    // If dbDistricts is empty, we fall back to the old logic (data not yet in firestore)
    if (dbDistricts.length === 0) {
      const districtsList: { id: string; name: string; regionId: string }[] = [];
      const schoolsList: { id: string; name: string; regionId: string; districtId: string }[] = [];
      
      selectedRegions.forEach(regionId => {
        const regionObj = allowedRegions.find(r => r.id === regionId);
        const regionName = regionObj ? regionObj.name : regionId;
        const config = getRegionConstraints(regionId, regionName);
        
        config.districts.forEach(d => {
          districtsList.push({ id: d, name: d, regionId });
        });
        config.schools.forEach(s => {
          schoolsList.push({ id: s, name: s, regionId, districtId: getFallbackDistrictId(s, regionId) });
        });
      });
      districtsList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      return { availableDistricts: districtsList, availableSchools: schoolsList, availableRegionSchools: schoolsList };
    }

    // New Firestore logic
    const districts = dbDistricts.filter(d => selectedRegions.includes(d.regionId));
    districts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    
    // Filter schools ONLY by regions, mapping to computed districtId if missing (e.g. for seeded schools)
    const schools = dbSchools.map(s => {
      const districtId = s.districtId || findDistrictForSchool(s.name, s.regionId, dbDistricts);
      return { ...s, districtId };
    }).filter(s => {
      if (s.regionId && selectedRegions.includes(s.regionId)) {
        return true;
      }
      const schoolDistrict = dbDistricts.find(d => d.id === s.districtId);
      return schoolDistrict && selectedRegions.includes(schoolDistrict.regionId);
    });
    
    return {
      availableDistricts: districts,
      availableRegionSchools: schools,
      availableSchools: schools,
    };
  }, [selectedRegions, allowedRegions, dbDistricts, dbSchools]);

  // Derive filtered schools based on district selection
  const availableSchools = useMemo(() => {
    console.log("Filtering institutions. rawAvailableSchools:", rawAvailableSchools.length, "selectedDistricts:", selectedDistricts);
    const filtered = selectedDistricts.length === 0 ? rawAvailableSchools : rawAvailableSchools.filter(s => selectedDistricts.includes(s.districtId));
    console.log("Filtered institutions result:", filtered.length);
    return filtered;
  }, [rawAvailableSchools, selectedDistricts]);

  // Sync selection state: auto-clean on region removal
  useEffect(() => {
    const validDistrictIds = availableDistricts.map(d => d.id);
    const validSchoolIds = availableSchools.map(s => s.id);
    
    setSelectedDistricts(prev => {
        const shouldRemove = prev.some(id => !validDistrictIds.includes(id));
        if (!shouldRemove) return prev;
        return prev.filter(id => validDistrictIds.includes(id));
    });
    setSelectedInstitutions(prev => {
        const shouldRemove = prev.some(id => !validSchoolIds.includes(id));
        if (!shouldRemove) return prev;
        return prev.filter(id => validSchoolIds.includes(id));
    });
  }, [availableDistricts, availableSchools]);

  // Smart Search filtered careers
  const subclustersMap = SUBCLUSTERS_MAP;

  const filteredSmartCareers = useMemo(() => {
    return uploadedCareers.filter(career => {
      const matchesQuery = !smartSearchQuery || 
        career.occupation.toLowerCase().includes(smartSearchQuery.toLowerCase()) ||
        (career.code && career.code.toLowerCase().includes(smartSearchQuery.toLowerCase()));
      
      const matchesCluster = !smartSearchCluster || career.cluster === smartSearchCluster;
      
      const matchesSubcluster = !smartSearchSubcluster || 
        (career.subclusters && career.subclusters.some((s: string) => s === smartSearchSubcluster));
      
      return matchesQuery && matchesCluster && matchesSubcluster;
    });
  }, [uploadedCareers, smartSearchQuery, smartSearchCluster, smartSearchSubcluster]);

  const smartClusterOptions = useMemo(() => {
    return Object.keys(subclustersMap);
  }, [subclustersMap]);

  const smartSubclusterOptions = useMemo(() => {
    if (smartSearchCluster) {
      return subclustersMap[smartSearchCluster] || [];
    }
    const all = new Set<string>();
    Object.values(subclustersMap).forEach(list => {
      list.forEach(item => all.add(item));
    });
    return Array.from(all).sort();
  }, [smartSearchCluster, subclustersMap]);

  const groupedSmartCareers = useMemo(() => {
    const groups: Record<string, typeof filteredSmartCareers> = {};
    filteredSmartCareers.forEach(career => {
      if (!groups[career.cluster]) {
        groups[career.cluster] = [];
      }
      groups[career.cluster].push(career);
    });
    // Sort each group alphabetically
    Object.values(groups).forEach(list => {
      list.sort((a, b) => (a.occupation || '').localeCompare(b.occupation || ''));
    });
    // Return sorted clusters
    return Object.keys(groups).sort().map(clusterName => ({
      cluster: clusterName,
      occupations: groups[clusterName]
    }));
  }, [filteredSmartCareers]);


  const availableOccupations = useMemo(() => {
    if (!careerCluster) return [];
    let filtered = uploadedCareers.filter(c => c.cluster === careerCluster);
    if (functionalFocus && functionalFocus !== 'General') {
      filtered = filtered.filter(c => c.subclusters.includes(functionalFocus));
    }
    const uniqueOccs = new Set(filtered.map(c => c.occupation));
    return Array.from(uniqueOccs).sort((a: any, b: any) => (String(a) || '').localeCompare(String(b) || ''));
  }, [uploadedCareers, careerCluster, functionalFocus]);

  // Oslo AI Content Engine helper state

  const [keywords, setKeywords] = useState('social media, customer service');
  const [draftingProgress, setDraftingProgress] = useState(false);
  const [draftingStatusMsg, setDraftingStatusMsg] = useState('');

  // Narrative inputs (to be auto-drafted / modified manually)
  const [overview, setOverview] = useState('');
  const [responsibilitiesText, setResponsibilitiesText] = useState('');
  const [qualificationsText, setQualificationsText] = useState('');
  const [learningObjective, setLearningObjective] = useState('');
  const [generatingObjective, setGeneratingObjective] = useState(false);

  // Candidate Questionnaire
  const [questions, setQuestions] = useState<Array<{ id: string; text: string }>>([]);

  // Posting authority fields for admins
  const [postAsType, setPostAsType] = useState<'organization' | 'external'>('organization');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [externalPartnerName, setExternalPartnerName] = useState<string>('');
  const [externalPartnerEmail, setExternalPartnerEmail] = useState<string>('');
  const [externalPartnerLogoUrl, setExternalPartnerLogoUrl] = useState<string>('');
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState<string | null>(null);

  // Opportunity Banner / Image selection state
  const [imageSelectionMode, setImageSelectionMode] = useState<'standard' | 'custom'>('standard');
  const [selectedStandardIndex, setSelectedStandardIndex] = useState<number>(0);
  const [customImageUrl, setCustomImageUrl] = useState<string>('');
  const [isOppCropOpen, setIsOppCropOpen] = useState(false);
  const [oppCropImageSrc, setOppCropImageSrc] = useState<string | null>(null);

  const availableSuperadminImages = useMemo(() => {
    if (!careerCluster) {
      return [];
    }
    let list: string[] = [];
    if (functionalFocus && functionalFocus !== 'General' && globalBranding?.subclusterImages?.[functionalFocus]) {
      list = (globalBranding.subclusterImages[functionalFocus] || []).filter((url: string) => url && url.trim() !== '');
    }
    if (list.length === 0 && careerCluster && globalBranding?.clusterImages?.[careerCluster]) {
      list = (globalBranding.clusterImages[careerCluster] || []).filter((url: string) => url && url.trim() !== '');
    }
    // Take at most 3 images from superadmin console
    list = list.slice(0, 3);

    // If none exist in superadmin console for this cluster, default to 1 fallback image
    if (list.length === 0) {
      const fallback = getOpportunityImage(careerCluster, formTitle, functionalFocus);
      list = [fallback];
    }
    return list;
  }, [functionalFocus, careerCluster, globalBranding, formTitle]);

  const existingOrgs = useMemo(() => {
    return (users || []).filter(u => u.role === 'organization');
  }, [users]);

  const handleLogoUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        showAlert({
          title: 'Invalid File Type',
          message: 'Please upload an image file (PNG, JPG, or WebP).',
          type: 'warning',
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setCropImageSrc(reader.result as string);
        setIsCropOpen(true);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  };

  const handleCropApply = (croppedFile: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setExternalPartnerLogoUrl(reader.result as string);
      setIsCropOpen(false);
      setCropImageSrc(null);
    };
    reader.readAsDataURL(croppedFile);
  };

  const handleOppImageUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (!file.type.startsWith('image/')) {
        showAlert({
          title: 'Invalid File Type',
          message: 'Please upload a valid image file (PNG, JPG, WebP).',
          type: 'warning',
        });
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setOppCropImageSrc(reader.result as string);
        setIsOppCropOpen(true);
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  };

  const handleOppCropApply = (croppedFile: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      setCustomImageUrl(reader.result as string);
      setImageSelectionMode('custom');
      setIsOppCropOpen(false);
      setOppCropImageSrc(null);
    };
    reader.readAsDataURL(croppedFile);
  };

  // When changing cluster, match appropriate initial focus
  const resetFormState = () => {
    setEditingOppId(null);
    setFormTitle('');
    setCareerCluster('');
    setFunctionalFocus('');
    setOccupationSelection('');
    setWorksiteAddress(activeUser?.address || activeTenant.address || '');
    setSemester(selectedTerm || 'Fall 2026');
    setRegistryDeadline('');
    setTargetLevel('High School');
    setOpeningsCount(1);
    setTotalHours(0);
    setSupervisorName('');
    setSupervisorTitle('');
    setCompensationType('paid');
    setPayStructure('Hourly Wage');
    setPayAmount('');
    setOverview('');
    setResponsibilitiesText('');
    setQualificationsText('');
    setLearningObjective('');
    setQuestions([]);
    setSelectedRegions([activeTenant.id]);
    const initRegion = getRegionConstraints(activeTenant.id, activeTenant.name);
    setSelectedDistricts(initRegion.districts);
    setSelectedInstitutions(initRegion.schools);
    setKeywords('');
    setPostAsType('organization');
    setSelectedOrgId('');
    setExternalPartnerName('');
    setExternalPartnerEmail('');
    setExternalPartnerLogoUrl('');
    setMinAge('');
    setMaxAge('');
    setImageSelectionMode('standard');
    setSelectedStandardIndex(0);
    setCustomImageUrl('');
  };

  const handleEditInitiation = (opp: Opportunity) => {
    resetFormState();
    setEditingOppId(opp.id);
    setFormTitle(opp.title);
    setCareerCluster(opp.careerCluster || 'Education');
    setFunctionalFocus(opp.subcluster || '');
    setOccupationSelection(opp.occupation || '');
    setWorksiteAddress(opp.location || '');
    setSemester(opp.semester || selectedTerm || 'Fall 2026');
    setRegistryDeadline(opp.applicationDeadline || '');
    setTargetLevel(opp.targetEducationLevel || 'High School');
    setOpeningsCount(opp.numOpenings || 1);
    setTotalHours(opp.hoursRequired || 0);
    setSupervisorName(opp.reportsTo || '');
    setSupervisorTitle(opp.supervisorTitle || '');
    setPayAmount(opp.hourlyRate || '');
    setPayStructure(opp.paymentStructure || 'Hourly Wage');
    setSelectedDistricts(opp.targetSchoolDistricts || []);
    setSelectedInstitutions(opp.targetSchools || []);
    setSelectedRegions(opp.targetRegionIds || [activeTenant.id]);

    if (opp.imageUrl && opp.imageUrl.trim()) {
      const matchIdx = availableSuperadminImages.findIndex(img => img === opp.imageUrl);
      if (matchIdx !== -1) {
        setImageSelectionMode('standard');
        setSelectedStandardIndex(matchIdx);
        setCustomImageUrl('');
      } else {
        setImageSelectionMode('custom');
        setCustomImageUrl(opp.imageUrl);
        setSelectedStandardIndex(0);
      }
    } else {
      setImageSelectionMode('standard');
      setSelectedStandardIndex(0);
      setCustomImageUrl('');
    }

    if (opp.minAge !== undefined && opp.minAge !== null) {
      setMinAge(opp.minAge.toString());
    } else {
      setMinAge('');
    }
    if (opp.maxAge !== undefined && opp.maxAge !== null) {
      setMaxAge(opp.maxAge.toString());
    } else {
      setMaxAge('');
    }

    if (opp.organizationId && opp.organizationId !== 'external-partner') {
      setPostAsType('organization');
      setSelectedOrgId(opp.organizationId);
      setExternalPartnerName('');
      setExternalPartnerEmail(opp.externalContactEmail || '');
      setExternalPartnerLogoUrl('');
    } else {
      setPostAsType('external');
      setSelectedOrgId('');
      setExternalPartnerName(opp.externalOrganizationName || opp.organizationName || '');
      setExternalPartnerEmail(opp.externalContactEmail || '');
      setExternalPartnerLogoUrl(opp.externalLogoUrl || '');
    }

    // description: `**Overview:** ${overview}\n\n**Responsibilities:** ${responsibilitiesText}`
    const desc = opp.description || '';
    const parts = desc.split('**Responsibilities:**');
    const overviewPart = parts[0].replace('**Overview:**', '').trim();
    const responsibilitiesPart = parts[1]?.trim() || '';
    setOverview(overviewPart);
    setResponsibilitiesText(responsibilitiesPart);
    
    // qualificationsText: qualifications or requirements.join('\n')
    setQualificationsText(opp.qualifications || (opp.requirements || []).join('\n'));
    setLearningObjective(opp.learningObjective || '');
    setKeywords(opp.skills || '');

    // Questionnaires
    setQuestions((opp.customQuestions || []).map((q, idx) => ({ id: `q-${idx}`, text: q })));
    
    setActiveTab('post');
  };

  const handleArchiveOpportunity = async (opp: Opportunity) => {
    const confirmMessage = `Are you sure you want to archive "${opp.title}"?\n\nBecause students may have already applied, and advisors may have recommended this posting, it will not be permanently deleted. Instead, it will be safely archived (soft-deleted). This keeps past applications, placements, and historical statistics intact while immediately removing this posting from student search results.`;
    
    const confirmed = await showConfirm({
      title: `Archive Opportunity`,
      message: confirmMessage,
      confirmText: 'Archive Opportunity',
      type: 'danger',
    });

    if (confirmed) {
      try {
        await updateOpportunity(opp.id, { status: 'archived' });
        // If the selected opportunity is the one being archived, clear selection or update its status
        if (selectedOpp && selectedOpp.id === opp.id) {
          setSelectedOpp(prev => prev ? { ...prev, status: 'archived' } : null);
          setActiveTab('all');
        }
        showToast({
          title: 'Opportunity Archived',
          message: `"${opp.title}" was archived successfully.`,
          type: 'info',
        });
      } catch (err: any) {
        showAlert({
          title: 'Archive Failed',
          message: 'Failed to archive opportunity: ' + err.message,
          type: 'error',
        });
      }
    }
  };

  const handleClusterChange = (cluster: string) => {
    setCareerCluster(cluster);
    setFunctionalFocus('');
    setOccupationSelection('');
  };

  // Questionnaire Helper handlers
  const handleAddQuestion = () => {
    const nextId = `q-${Date.now()}`;
    setQuestions([...questions, { id: nextId, text: '' }]);
  };

  const handleQuestionTextChange = (id: string, text: string) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, text } : q));
  };

  const handleRemoveQuestion = (id: string) => {
    if (questions.length === 1) return; // leave at least 1
    setQuestions(questions.filter(q => q.id !== id));
  };

  // Resolve target organization's profile to enrich AI generation
  const getSelectedOrgProfile = () => {
    const isAdmin = activeUser?.role === 'superadmin' || activeUser?.role === 'regional_admin';
    if (isAdmin) {
      if (postAsType === 'organization') {
        const selectedOrg = (users || []).find(u => u.uid === selectedOrgId);
        return {
          orgName: selectedOrg?.orgName || selectedOrg?.name || 'Selected Portal Organization',
          industry: selectedOrg?.industry || '',
          description: selectedOrg?.description || '',
          companyProfileText: selectedOrg?.companyProfileText || '',
          onboardingText: selectedOrg?.onboardingText || '',
          website: selectedOrg?.website || '',
        };
      } else {
        return {
          orgName: externalPartnerName || 'External Partner Org',
          contactEmail: externalPartnerEmail || '',
          isExternal: true,
        };
      }
    } else {
      return {
        orgName: activeUser?.orgName || activeUser?.name || 'WBL Partner Organization',
        industry: activeUser?.industry || '',
        description: activeUser?.description || '',
        companyProfileText: activeUser?.companyProfileText || '',
        onboardingText: activeUser?.onboardingText || '',
        website: activeUser?.website || '',
      };
    }
  };

  // Oslo AI Assistant Learning Objective Helper
  const handleGenerateLearningObjective = async () => {
    const effectiveTitle = (formTitle || '').trim() || occupationSelection || (functionalFocus && functionalFocus !== 'General' ? `${functionalFocus} Intern` : (careerCluster ? `${careerCluster} Intern` : 'Student Placement'));
    if (!formTitle.trim() && effectiveTitle) {
      setFormTitle(effectiveTitle);
    }

    setGeneratingObjective(true);
    try {
      const response = await fetch('/api/generate-objective', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: effectiveTitle,
          careerCluster,
          subcluster: functionalFocus,
          focus: functionalFocus,
          occupation: occupationSelection,
          targetLevel,
          keywords,
          orgProfile: getSelectedOrgProfile(),
          salt: `${Date.now()}_${Math.random()}`
        })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('application/json')) {
        throw new Error("Unable to contact Oslo assistant. Utilizing local fallback.");
      }

      const data = await response.json().catch(() => ({}));
      setLearningObjective(data.objective || '');
    } catch (err) {
      console.warn("Generating on-the-fly offline learning objective...", err);
      const spec = occupationSelection || (functionalFocus && functionalFocus !== 'General' ? functionalFocus : careerCluster) || 'practical workplace skills';
      setLearningObjective(`During this ${effectiveTitle} placement, the student will gain hands-on, real-world experience in ${spec}. They will build essential professional skills, collaborate with experienced team members, and learn the day-to-day operations of a dynamic workspace in the ${careerCluster || 'career pathway'} sector.`);
    } finally {
      setGeneratingObjective(false);
    }
  };

  // Trigger Oslo generative drafting helper
  const handleTriggerDraft = async () => {
    const effectiveTitle = (formTitle || '').trim() || occupationSelection || (functionalFocus && functionalFocus !== 'General' ? `${functionalFocus} Intern` : (careerCluster ? `${careerCluster} Intern` : 'Placement Intern'));
    if (!formTitle.trim() && effectiveTitle) {
      setFormTitle(effectiveTitle);
    }

    setDraftingProgress(true);
    const messages = [
      `Synthesizing ${careerCluster || 'Career Pathway'} standards...`,
      functionalFocus && functionalFocus !== 'General' ? `Grounding in ${functionalFocus} focus...` : `Structuring role responsibilities...`,
      occupationSelection ? `Customizing tasks for ${occupationSelection}...` : `Refining occupational workflows...`,
      `Polishing coherent job description and objectives...`
    ];

    let msgIdx = 0;
    setDraftingStatusMsg(messages[0]);
    const interval = setInterval(() => {
      msgIdx = (msgIdx + 1) % messages.length;
      setDraftingStatusMsg(messages[msgIdx]);
    }, 850);

    try {
      const response = await fetch('/api/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: effectiveTitle,
          careerCluster,
          subcluster: functionalFocus,
          focus: functionalFocus,
          occupation: occupationSelection,
          targetLevel,
          keywords,
          orgProfile: getSelectedOrgProfile(),
          salt: `${Date.now()}_${Math.random()}`
        })
      });

      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('application/json')) {
        throw new Error("Local engine fallback utilized.");
      }

      const data = await response.json().catch(() => ({}));
      setOverview(data.overview);
      setResponsibilitiesText(data.responsibilities);
      setQualificationsText(data.qualifications);
      if (data.objective) {
        setLearningObjective(data.objective);
      }

    } catch (err) {
      console.warn("Generating on-the-fly offline metadata...", err);
      const spec = occupationSelection || (functionalFocus && functionalFocus !== 'General' ? functionalFocus : careerCluster) || 'applied practice';
      setOverview(`At ${getSelectedOrgProfile()?.orgName || 'our organization'}, the ${effectiveTitle} intern will engage in guided operational tasks, project support, and workplace safety under experienced mentorship in the ${careerCluster || 'industry'} sector${functionalFocus ? ` (${functionalFocus})` : ''}. Students gain practical exposure to real-world workflows, professional tools, and team collaboration. Dedicated supervisors provide continuous feedback to nurture career readiness.`);
      setResponsibilitiesText(`• Support the team in executing daily tasks and project deliverables in ${spec}.\n• Maintain active worksite safety standards, professional etiquette, and organized digital records.\n• Shadow experienced mentors during complex procedures to learn industry best practices.\n• Prepare regular progress summaries and present task updates during supervisor check-ins.`);
      setQualificationsText(`• Active interest in career pathways within ${careerCluster || 'the field'}${functionalFocus ? ` and ${functionalFocus}` : ''}.\n• Strong communication skills, dependability, and willingness to ask thoughtful questions.\n• Basic familiarity with digital tools and adherence to workplace safety regulations.\n• Positive attitude and commitment to collaborative teamwork.`);
      setLearningObjective(`Through this ${effectiveTitle} placement, the student will master practical skills in ${spec}, learn industry-standard workflows, and develop confidence for future career pathways in ${careerCluster || 'their chosen field'}.`);
    } finally {
      clearInterval(interval);
      setDraftingProgress(false);
      setDraftingStatusMsg('');
    }
  };

  // Submit the entire posting config!
  const handleFullPostSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle || !overview) {
      showAlert({
        title: "Missing Information",
        message: "Please ensure Placement Title is specified and Narrative & Content are drafted/written.",
        type: 'warning',
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const parsedReqs = qualificationsText
        .split('\n')
        .map(q => q.trim())
        .filter(q => q.length > 0);

      const parsedQuestions = questions.map(q => q.text).filter(q => q.length > 0);

      // Replicate across selected regional tenants in our database!
      const validRegions = selectedRegions.filter(id => allowedRegions.some(ar => ar.id === id));
      const targetRegionIds = validRegions.length > 0 ? validRegions : [activeTenant.id];
      
      // Determine organization posting details (for superadmin / regional admin override)
      const isAdmin = activeUser?.role === 'superadmin' || activeUser?.role === 'regional_admin';
      
      let finalOrgId: string | null = activeUser?.role === 'organization'
        ? getCanonicalOrgTeamId(activeUser, users)
        : (activeUser?.uid || 'guest-author');
      let finalOrgName = activeUser?.orgName || 'Training WBL Partner Org';
      let finalExternalOrgName = '';
      let finalExternalContactEmail = '';
      let rawExternalLogoUrl = activeUser?.avatar || '';

      if (isAdmin) {
        if (postAsType === 'organization') {
          const selectedOrg = (users || []).find(u => u.uid === selectedOrgId);
          finalOrgId = selectedOrgId || 'selected-org';
          finalOrgName = selectedOrg?.orgName || selectedOrg?.name || 'Selected Portal Organization';
          finalExternalOrgName = '';
          finalExternalContactEmail = selectedOrg?.email || '';
          rawExternalLogoUrl = selectedOrg?.avatar || '';
        } else {
          finalOrgId = 'external-partner';
          finalOrgName = externalPartnerName || 'External Partner Org';
          finalExternalOrgName = externalPartnerName || '';
          finalExternalContactEmail = externalPartnerEmail.trim();
          rawExternalLogoUrl = externalPartnerLogoUrl || '';
        }
      } else {
        finalExternalContactEmail = activeUser?.email || '';
      }

      const effectiveSuperadminImage = availableSuperadminImages[selectedStandardIndex] || availableSuperadminImages[0] || '';
      const rawOpportunityImageUrl = imageSelectionMode === 'custom'
        ? customImageUrl.trim()
        : effectiveSuperadminImage;

      // Ensure base64 images are cleanly compressed to prevent Firestore 1MB document limit errors
      const [finalOpportunityImageUrl, finalExternalLogoUrl] = await Promise.all([
        compressImageIfDataUrl(rawOpportunityImageUrl, 1200, 0.82),
        compressImageIfDataUrl(rawExternalLogoUrl, 512, 0.85)
      ]);

      if (editingOppId) {
        // UPDATE MODE
        await updateOpportunity(editingOppId, {
          title: formTitle,
          description: `**Overview:** ${overview}\n\n**Responsibilities:** ${responsibilitiesText}`,
          requirements: parsedReqs.length > 0 ? parsedReqs : ['Basic background matching standard pathways coordinates.'],
          location: worksiteAddress,
          status: isOpenState ? 'open' : 'closed',
          type: 'internship', // default WBL category mapping
          hoursRequired: totalHours,
          careerCluster: careerCluster,
          subcluster: functionalFocus,
          occupation: occupationSelection,
          targetRegionIds: targetRegionIds,
          targetSchoolDistricts: selectedDistricts,
          targetSchools: selectedInstitutions,
          numOpenings: openingsCount,
          semester: semester,
          targetEducationLevel: targetLevel,
          customQuestions: parsedQuestions,
          supervisorTitle: supervisorTitle,
          applicationDeadline: registryDeadline,
          reportsTo: supervisorName,
          hourlyRate: payAmount,
          paymentStructure: payStructure,
          externalLogoUrl: finalExternalLogoUrl,
          imageUrl: finalOpportunityImageUrl,
          qualifications: qualificationsText,
          skills: keywords,
          learningObjective: learningObjective,
          organizationId: finalOrgId,
          organizationName: finalOrgName,
          externalOrganizationName: finalExternalOrgName,
          externalContactEmail: finalExternalContactEmail || undefined,
          minAge: minAge ? parseInt(minAge, 10) : null,
          maxAge: maxAge ? parseInt(maxAge, 10) : null,
          postedByUserId: activeUser?.uid,
          postedByUserEmail: activeUser?.email,
          postedByName: activeUser?.name,
          postedByRole: activeUser?.role,
          isProxyPosting: isAdmin,
          isExternalPartner: isAdmin && (postAsType === 'external' || finalOrgId === 'external-partner'),
          createdBy: activeUser?.email
        });
      } else {
        // CREATE MODE
        for (const tenantId of targetRegionIds) {
          await addOpportunity({
            tenantId: tenantId,
            organizationId: finalOrgId,
            organizationName: finalOrgName,
            title: formTitle,
            description: `**Overview:** ${overview}\n\n**Responsibilities:** ${responsibilitiesText}`,
            requirements: parsedReqs.length > 0 ? parsedReqs : ['Basic background matching standard pathways coordinates.'],
            location: worksiteAddress,
            status: isOpenState ? 'open' : 'closed',
            type: 'internship', // default WBL category mapping
            hoursRequired: totalHours,
            careerCluster: careerCluster,
            subcluster: functionalFocus,
            occupation: occupationSelection,
            targetRegionIds: targetRegionIds,
            targetSchoolDistricts: selectedDistricts,
            targetSchools: selectedInstitutions,
            numOpenings: openingsCount,
            semester: semester,
            targetEducationLevel: targetLevel,
            customQuestions: parsedQuestions,
            supervisorTitle: supervisorTitle,
            applicationDeadline: registryDeadline,
            reportsTo: supervisorName,
            hourlyRate: payAmount,
            paymentStructure: payStructure,
            externalLogoUrl: finalExternalLogoUrl,
            imageUrl: finalOpportunityImageUrl,
            qualifications: qualificationsText,
            skills: keywords,
            learningObjective: learningObjective,
            externalOrganizationName: finalExternalOrgName,
            externalContactEmail: finalExternalContactEmail || undefined,
            minAge: minAge ? parseInt(minAge, 10) : null,
            maxAge: maxAge ? parseInt(maxAge, 10) : null,
            postedByUserId: activeUser?.uid,
            postedByUserEmail: activeUser?.email,
            postedByName: activeUser?.name,
            postedByRole: activeUser?.role,
            isProxyPosting: isAdmin,
            isExternalPartner: isAdmin && (postAsType === 'external' || finalOrgId === 'external-partner'),
            createdBy: activeUser?.email
          });
        }
      }

      setSubmissionSuccess(true);
      setIsSubmitting(false);

      showToast({
        title: editingOppId ? "Posting Updated" : "Opportunity Published",
        message: `"${formTitle}" was successfully ${editingOppId ? 'updated' : 'published'}!`,
        type: 'success',
      });

      // Reset
      setActiveTab('all');
      setSubmissionSuccess(false);
      resetFormState();
    } catch (err: any) {
      setIsSubmitting(false);
      showAlert({
        title: "Error Saving Opportunity",
        message: "Error saving opportunity configuration: " + err.message,
        type: 'error',
      });
    }
  };

  // Student application trigger
  const handleApplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOppForApply) return;
    try {
      const questionsToAnswer = selectedOppForApply.customQuestions && selectedOppForApply.customQuestions.length > 0
        ? selectedOppForApply.customQuestions
        : [];

      const formattedLetter = questionsToAnswer.map((q, idx) => {
        const ans = questionAnswers[idx] || '';
        return `Question: ${q}\nAnswer: ${ans}`;
      }).join('\n\n');

      const formattedSummary = "Completed pathway-specific questionnaire responses.";

      await applyToOpportunity(selectedOppForApply.id, formattedLetter, formattedSummary);
      const appliedOppTitle = selectedOppForApply.title;
      setSelectedOppForApply(null);
      setCoverLetter('');
      setResumeSummary('');
      setQuestionAnswers({});
      showAlert({
        title: "Application Submitted",
        message: `Your application for "${appliedOppTitle}" has been successfully submitted! The employer and coordinators will review your profile.`,
        type: 'success',
      });
    } catch (err: any) {
      showAlert({
        title: "Submission Error",
        message: "Failed submitting placement application: " + err.message,
        type: 'error',
      });
    }
  };

  // Filter opportunities to screen lists
  const filteredOpportunities = useMemo(() => {
    const filtered = opportunities.filter(opp => {
      // 0. Filter by selected term
      if (opp.semester !== selectedTerm && opp.term !== selectedTerm && (opp.semester || opp.term)) {
          // If the opportunity has a term/semester defined and it doesn't match, filter it out
          // If it has NO term/semester, we might want to show it or not. Usually show it if it's "all terms" or untagged.
          return false;
      }

      // Filter out archived/soft-deleted postings
      if (opp.status === 'archived') {
        const canSeeArchived = activeUser && (activeUser.uid === opp.createdBy || activeUser.uid === opp.organizationId || doesRecordBelongToOrg(opp, activeUser, users) || activeUser.role === 'regional_admin' || activeUser.role === 'superadmin');
        if (!canSeeArchived || !showMyPostings) return false;
      }

      // 1. Check open/closed status
      if (!showClosedRoles && opp.status !== 'open') return false;

      // 2. Search query matching
      const matchesSearch = 
        opp.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opp.organizationName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opp.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        opp.location.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      // 3. Extracted Filters
      if (filterCluster !== 'all' && opp.careerCluster !== filterCluster) return false;
      if (filterFunction !== 'all' && opp.jobFunction !== filterFunction) return false;
      if (filterCompensation !== 'all') {
        const hasComp = !!opp.compensation || !!opp.hourlyRate;
        if (filterCompensation === 'paid' && !hasComp) return false;
        if (filterCompensation === 'unpaid' && hasComp) return false;
      }

      // 4. My Postings Toggle
      if (showMyPostings && activeUser) {
        if (activeUser.uid !== opp.createdBy && activeUser.uid !== opp.organizationId && !doesRecordBelongToOrg(opp, activeUser, users)) {
          return false;
        }
      }

      // Applied Only Toggle for students
      if (showAppliedOnly && activeUser && activeUser.role === 'student') {
        const hasApplied = myApplications.some(app => app.opportunityId === opp.id);
        if (!hasApplied) return false;
      }

      // Advisor Restricted Filter:
      // Default: filter the opportunities to NOT show opportunities that are restricted for schools that are not theirs.
      // If showRestricted is toggled ON, show those restricted opportunities.
      if (activeUser && activeUser.role === 'advisor' && !showRestricted) {
        if (isOppRestrictedForOtherSchools(opp, activeUser, dbDistricts, dbSchools, allSchools)) {
          return false;
        }
      }

      // 5. Student District, School, and Age constraints are checked at detail view/application level
      // so students can see all placements but are blocked from applying.

      // 6. Tenant-specific Education Level Constraints
      if (activeTenant && activeTenant.id !== 'global') {
        if (opp.targetEducationLevel === 'High School' && activeTenant.highSchoolActive === false) return false;
        if (opp.targetEducationLevel === 'Post-Secondary' && activeTenant.postSecondaryActive === false) return false;
      }

      return true;
    });

    // Now, sort the filtered list
    return filtered.sort((a, b) => {
      if (sortBy === 'newest') {
        return getOpportunityTimestamp(b.createdAt) - getOpportunityTimestamp(a.createdAt);
      }
      if (sortBy === 'oldest') {
        return getOpportunityTimestamp(a.createdAt) - getOpportunityTimestamp(b.createdAt);
      }
      if (sortBy === 'title-asc') {
        return (a.title || '').localeCompare(b.title || '');
      }
      if (sortBy === 'title-desc') {
        return (b.title || '').localeCompare(a.title || '');
      }
      if (sortBy === 'paid-first') {
        const aHasComp = !!a.compensation || !!a.hourlyRate;
        const bHasComp = !!b.compensation || !!b.hourlyRate;
        if (aHasComp && !bHasComp) return -1;
        if (!aHasComp && bHasComp) return 1;
        return getOpportunityTimestamp(b.createdAt) - getOpportunityTimestamp(a.createdAt);
      }
      if (sortBy === 'hours-desc') {
        return (b.hoursRequired || 0) - (a.hoursRequired || 0);
      }
      if (sortBy === 'hours-asc') {
        return (a.hoursRequired || 0) - (b.hoursRequired || 0);
      }
      return 0;
    });
  }, [opportunities, searchQuery, filterCluster, filterFunction, filterCompensation, showClosedRoles, showMyPostings, showAppliedOnly, showRestricted, myApplications, activeUser, dbDistricts, dbSchools, allSchools, selectedTerm, sortBy, activeTenant]);

  return (
    <div className="font-sans text-xs">
      {/* Hero Header Banner (Cinematic Retreat) - Omitted on standalone details page view */}
      {activeTab !== 'details' && (
        <HeroBanner
          title="OPPORTUNITIES"
          subtitle="EXPLORE VETTED PLACEMENTS & VOCATIONAL WORK ROLES"
          pageKey="opportunities"
          height={activeTab === 'post' ? '0px' : '187.5px'}
          className={activeTab === 'post' ? 'opacity-0 mb-0' : 'opacity-100 mb-6'}
        />
      )}

      {/* Main viewport area */}
      <div 
        className={`w-[95%] mx-auto space-y-6 ${activeTab === 'details' ? 'py-6' : 'mt-6'}`}
        ref={detailPageRef}
      >
        {activeTab === 'all' && (
          <motion.div
            key="discovery"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
                        {/* Filter controls */}
            <div className="bg-white rounded-xl border border-slate-200/70 p-4 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-3">
              
              {/* Search */}
              <SearchBar 
                value={searchQuery} 
                onChange={setSearchQuery} 
                placeholder="Search opportunities (title, organization, keyword)..." 
              />

              {/* Selectors and Action Button */}
              <div className="flex flex-wrap items-center gap-4">
                {/* My Postings Toggle (Only for organizations / admins) */}
                {activeUser && (activeUser.role === 'superadmin' || activeUser.role === 'regional_admin' || activeUser.role === 'organization') && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-500 tracking-widest uppercase">MY POSTINGS</span>
                    <button
                      type="button"
                      onClick={() => setShowMyPostings(!showMyPostings)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showMyPostings ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showMyPostings ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                )}

                {/* Applied Only Toggle (Only for students) */}
                {activeUser && activeUser.role === 'student' && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-500 tracking-widest uppercase">APPLIED ONLY</span>
                    <button
                      type="button"
                      onClick={() => setShowAppliedOnly(!showAppliedOnly)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showAppliedOnly ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                      id="applied-only-toggle"
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showAppliedOnly ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                )}

                {/* Show Restricted Toggle (Only for advisors) */}
                {activeUser && activeUser.role === 'advisor' && (
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-slate-500 tracking-widest uppercase">SHOW RESTRICTED</span>
                    <button
                      type="button"
                      onClick={() => setShowRestricted(!showRestricted)}
                      className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showRestricted ? 'bg-[#0094c8]' : 'bg-slate-200'}`}
                      id="advisor-show-restricted-toggle"
                      title={showRestricted ? "Showing opportunities restricted to other schools" : "Default: Filtered to opportunities for your schools"}
                    >
                      <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showRestricted ? 'translate-x-4' : 'translate-x-0'}`} />
                    </button>
                  </div>
                )}

                {/* Filter Catalog Popover */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsFilterPopoverOpen(!isFilterPopoverOpen)}
                    className="bg-white border border-slate-200 hover:border-slate-300 outline-none rounded-lg px-4 py-2 text-slate-700 font-bold flex items-center gap-2 text-[10px] tracking-widest uppercase shadow-sm transition-all cursor-pointer"
                  >
                    <Filter className="w-3.5 h-3.5" /> FILTER CATALOG
                  </button>

                  {isFilterPopoverOpen && (
                    <>
                      {/* Invisible backdrop to close popover */}
                      <div className="fixed inset-0 z-40" onClick={() => setIsFilterPopoverOpen(false)} />
                      
                      <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 z-50 p-5 space-y-5 animate-in fade-in zoom-in duration-200">
                        {/* Placement Cluster */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black text-[#5c6ce8] uppercase tracking-widest block">PLACEMENT CLUSTER</label>
                          <select 
                            value={filterCluster}
                            onChange={(e) => {
                              setFilterCluster(e.target.value);
                              setFilterFunction('all'); // Reset function when cluster changes
                            }}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0094c8] appearance-none cursor-pointer"
                          >
                            <option value="all">ALL CLUSTERS</option>
                            {CAREER_CLUSTERS.slice().sort((a,b) => (a.label || '').localeCompare(b.label || '')).map(cluster => (
                              <option key={cluster.id} value={cluster.label}>{cluster.label}</option>
                            ))}
                          </select>
                        </div>

                        {/* Job Function */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black text-[#5c6ce8] uppercase tracking-widest block">JOB FUNCTION</label>
                          <select 
                            value={filterFunction}
                            onChange={(e) => setFilterFunction(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0094c8] appearance-none cursor-pointer disabled:opacity-50"
                            disabled={filterCluster === 'all'}
                          >
                            <option value="all">ALL FUNCTIONS</option>
                            {filterCluster !== 'all' && CAREER_CLUSTERS.find(c => c.label === filterCluster)?.skills.map((skill, idx) => (
                              <option key={`${skill}-${idx}`} value={skill}>{skill}</option>
                            ))}
                          </select>
                        </div>

                        {/* Compensation */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black text-[#5c6ce8] uppercase tracking-widest block">COMPENSATION</label>
                          <select 
                            value={filterCompensation}
                            onChange={(e) => setFilterCompensation(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0094c8] appearance-none cursor-pointer"
                          >
                            <option value="all">ALL STATUS</option>
                            <option value="paid">Paid</option>
                            <option value="unpaid">Unpaid</option>
                          </select>
                        </div>

                        {/* Sort By Option */}
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-black text-[#5c6ce8] uppercase tracking-widest block">SORT BY</label>
                          <select 
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs font-bold text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0094c8] appearance-none cursor-pointer"
                          >
                            <option value="newest">Newest First</option>
                            <option value="oldest">Oldest First</option>
                            <option value="title-asc">Title (A - Z)</option>
                            <option value="title-desc">Title (Z - A)</option>
                            <option value="paid-first">Paid First</option>
                            <option value="hours-desc">Hours (High to Low)</option>
                            <option value="hours-asc">Hours (Low to High)</option>
                          </select>
                        </div>

                        <hr className="border-slate-100" />

                        {/* Closed Roles Toggle */}
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-[10px] font-black text-slate-800 tracking-widest uppercase block">SHOW CLOSED ROLES</span>
                            <span className="text-[8px] font-bold text-slate-400 tracking-wide uppercase block mt-0.5">INCLUDE INACTIVE POSTINGS</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowClosedRoles(!showClosedRoles)}
                            className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${showClosedRoles ? 'bg-slate-800' : 'bg-slate-200'}`}
                          >
                            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${showClosedRoles ? 'translate-x-4' : 'translate-x-0'}`} />
                          </button>
                        </div>

                        <hr className="border-slate-100" />
                        
                        {/* Reset Filters */}
                        <button
                          onClick={() => {
                            setFilterCluster('all');
                            setFilterFunction('all');
                            setFilterCompensation('all');
                            setSortBy('newest');
                            setShowClosedRoles(false);
                            setSearchQuery('');
                            setShowAppliedOnly(false);
                          }}
                          className="w-full text-center text-[10px] font-black text-slate-500 hover:text-slate-700 cursor-pointer uppercase tracking-widest transition-colors py-2"
                        >
                          RESET FILTERS
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* New Opportunity Button */}
                {activeUser && (activeUser.role === 'superadmin' || activeUser.role === 'regional_admin' || activeUser.role === 'organization') && (
                  <button
                    type="button"
                    onClick={() => { resetFormState(); setActiveTab('post'); }}
                    className="px-4 py-2 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-lg text-xs font-bold transition-all shadow-sm flex items-center space-x-1.5 cursor-pointer border border-[#0094c8]/50"
                    id="btn-post-vacancy-tab"
                  >
                    <Plus className="w-3.5 h-3.5 text-white" />
                    <span>New Opportunity</span>
                  </button>
                )}
              </div>

            </div>            {/* List / Results Cards */}
            {filteredOpportunities.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200/60 p-12 text-center text-slate-400">
                <Briefcase className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                <p className="font-semibold text-slate-600 mb-1">No vacancies match your search coordinates.</p>
                <p className="text-[10px] text-slate-400">Try loosening your search filters or posting a new rotation role.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {filteredOpportunities.map(opp => {
                  const hasApplied = myApplications.some(app => app.opportunityId === opp.id);
                  const application = myApplications.find(app => app.opportunityId === opp.id);
                  const relatedTenant = (tenants || []).find(t => t.id === opp.tenantId);
                  const isMyOrg = activeUser && (opp.organizationId === activeUser.uid || doesRecordBelongToOrg(opp, activeUser, users));
                  const orgProfile = (users || []).find(u => u.uid === opp.organizationId || (u.role === 'organization' && u.orgTeamId === opp.organizationId));
                  const eligibility = getStudentEligibility(opp, activeUser, dbDistricts, dbSchools);

                  let cardBgBorderClass = 'bg-white border-slate-200/80 hover:border-slate-300';
                  if (isMyOrg) {
                    cardBgBorderClass = 'bg-sky-50 border-sky-300';
                  } else if (activeUser?.role === 'student' && application) {
                    if (application.status === 'accepted') {
                      cardBgBorderClass = 'bg-[#f0fdf4]/80 border-[#bbf7d0] hover:border-[#86efac]';
                    } else if (application.status === 'rejected') {
                      cardBgBorderClass = 'bg-[#fef2f2]/80 border-[#fecaca] hover:border-[#fca5a5]';
                    } else {
                      cardBgBorderClass = 'bg-[#eff6ff]/80 border-[#bfdbfe] hover:border-[#93c5fd]';
                    }
                  }
                  
                  return (
                    <div
                      key={opp.id}
                      className={`border rounded-2xl overflow-hidden shadow-xs hover:shadow-md transition-all flex flex-col md:flex-row group h-fit ${cardBgBorderClass}`}
                      id={`opp-card-${opp.id}`}
                    >
                      {/* Left Column: Image with Gradient Overlays */}
                      <div className="relative w-full md:w-[20%] h-40 md:h-auto self-stretch flex-shrink-0 overflow-hidden bg-slate-100">
                        <img
                          src={getOpportunityImage(opp.careerCluster, opp.title, opp.subcluster, globalBranding, opp.id, opp.imageUrl)}
                          alt={opp.title}
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-102"
                          referrerPolicy="no-referrer"
                        />
                        {/* Dark gradient overlay matching the image layout */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/35 to-transparent flex flex-col justify-end p-4">
                          {(() => {
                            const cats = resolveOpportunityCategories(opp);
                            return (
                              <>
                                  <span className="text-[10px] font-black text-white tracking-widest uppercase mb-0.5 truncate max-w-full font-sans">
                                    {cats.subcluster?.toUpperCase() || 'GENERAL PATHWAY'}
                                  </span>
                                  <span className="text-[9px] font-normal tracking-[0.16em] text-slate-300 uppercase truncate max-w-full font-sans">
                                    {cats.careerCluster?.toUpperCase() || 'CAREER EXPLORATION'}
                                  </span>
                              </>
                            );
                          })()}
                        </div>
                      </div>

                      {/* Right Column: Detailed content & call-to-actions */}
                      <div className="flex-1 p-5 md:p-6 flex flex-col justify-between gap-3.5">
                        <div className="space-y-3">
                          <div className="flex items-start gap-3.5">
                            {/* Rounded tile for Logo */}
                            <div className="w-20 h-20 md:w-24 md:h-24 bg-white border border-slate-200/80 rounded-2xl p-1 flex items-center justify-center shadow-2xs flex-shrink-0 self-start overflow-hidden">
                              {(() => {
                                const orgLogo = orgProfile?.avatar || opp.externalLogoUrl || relatedTenant?.logoUrl || relatedTenant?.avatar;
                                return getOrganizationLogo(opp.externalOrganizationName || opp.organizationName, orgLogo, opp.opportunityId || opp.organizationId, opp.careerCluster || opp.industry);
                              })()}
                            </div>

                            {/* Title stack */}
                            <div className="space-y-0.5 flex-1">
                              <div className="flex justify-between items-start gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="text-base md:text-[17px] font-bold text-[#0f2942] tracking-tight leading-snug">
                                    {opp.title}
                                  </h3>
                                  {opp.status === 'archived' && (
                                    <span className="text-[9px] font-black text-rose-700 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100 whitespace-nowrap uppercase tracking-widest">
                                      Archived
                                    </span>
                                  )}
                                  {opp.status === 'closed' && (
                                    <span className="text-[9px] font-black text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-100 whitespace-nowrap uppercase tracking-widest">
                                      Closed
                                    </span>
                                  )}
                                </div>
                                <div className="flex flex-col items-end gap-1.5">
                                  {opp.numOpenings && (
                                    <span className="text-[9px] font-black text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-100 whitespace-nowrap uppercase tracking-widest">
                                      {opp.numOpenings} Openings
                                    </span>
                                  )}
                                  {!eligibility.eligible && activeUser?.role === 'student' && (
                                    <span className="text-[9px] font-black text-rose-700 bg-rose-50 px-2 py-0.5 rounded-md border border-rose-100 whitespace-nowrap uppercase tracking-widest flex items-center gap-1 shadow-2xs">
                                      <XCircle className="w-3 h-3 text-rose-600" />
                                      Ineligible
                                    </span>
                                  )}
                                  {activeUser?.role === 'advisor' && isOppRestrictedForOtherSchools(opp, activeUser, dbDistricts, dbSchools, allSchools) && (
                                    <span className="text-[9px] font-black text-amber-700 bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200 whitespace-nowrap uppercase tracking-widest flex items-center gap-1 shadow-2xs" title="Restricted to other schools">
                                      <ShieldAlert className="w-3 h-3 text-amber-600" />
                                      Restricted
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mb-2">
                                <span className="text-[10px] font-extrabold tracking-wider text-slate-500 uppercase flex items-center gap-1.5">
                                  <span>{opp.externalOrganizationName || opp.organizationName}</span>
                                  {orgProfile?.verified && (
                                    <span className="inline-flex items-center gap-0.5 text-[8px] font-black text-sky-700 bg-sky-50 px-1.5 py-0.2 border border-sky-100 rounded uppercase tracking-widest">
                                      <Check className="w-2.5 h-2.5 text-sky-600" />
                                      Verified
                                    </span>
                                  )}
                                </span>
                                <span className="text-[10px] font-bold tracking-widest text-[#b0bec5] uppercase font-mono">
                                  {relatedTenant ? relatedTenant.name : (opp.regionId || opp.tenantId)}
                                </span>
                              </div>

                              {/* Metadata list (Location, Hours, Pay, Posted) on separate lines */}
                              <div className="flex flex-col gap-y-1.5 text-[10px] font-semibold text-slate-500 font-mono mb-1">
                                <div className="flex items-center space-x-2">
                                  <MapPin className="w-3.5 h-3.5 text-[#0091ca]/70" />
                                  <span className="truncate">{opp.location || opp.address}</span>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                                  <span>{opp.totalHours || opp.hoursRequired} Hours Required</span>
                                </div>

                                <div className="flex items-center space-x-2">
                                  <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                                  <span className={opp.paymentStructure === 'Unpaid' ? 'text-slate-400' : 'text-emerald-700 font-bold'}>
                                    {opp.paymentStructure === 'Unpaid' 
                                      ? 'Unpaid / Credit Only' 
                                      : `Paid: $${opp.hourlyRate || '0.00'} (${opp.paymentStructure || 'Hourly Wage'})`}
                                  </span>
                                </div>

                                <div className="flex items-center space-x-2 text-slate-400 italic">
                                  <Calendar className="w-3.5 h-3.5 text-slate-300" />
                                  <span>Posted: {formatOpportunityDate(opp.createdAt)}</span>
                                </div>

                                {opp.externalContactEmail && (
                                  <div className="flex items-center space-x-2 text-sky-700">
                                    <Mail className="w-3.5 h-3.5 text-[#0094c8] shrink-0" />
                                    <span className="truncate lowercase font-mono text-[10px] font-semibold">{opp.externalContactEmail}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Description summary */}
                          <p className="text-slate-600 leading-normal text-xs md:text-[12.5px] line-clamp-2">
                            {getCleanExcerpt(opp.description)}
                          </p>
                        </div>

                        {/* Interactive trigger footer row */}
                        <div className="flex flex-wrap items-center justify-end gap-3 pt-0.5">
                          <div className="flex items-center space-x-2">
                            {/* Edit Button */}
                            {activeUser && (activeUser.uid === opp.organizationId || doesRecordBelongToOrg(opp, activeUser, users) || activeUser.role === 'regional_admin' || activeUser.role === 'superadmin') && (
                              <button
                                onClick={() => handleEditInitiation(opp)}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-[11px] uppercase px-4 py-2 rounded-lg tracking-widest flex items-center gap-2 select-none duration-150 active:scale-95 transition-all cursor-pointer shadow-xs border border-slate-200"
                              >
                                  Edit
                              </button>
                            )}
                            
                            {activeUser?.role === 'student' ? (
                              <button
                                onClick={() => handleViewOpportunity(opp)}
                                className={`${
                                  application
                                    ? application.status === 'accepted'
                                      ? 'bg-emerald-600 hover:bg-emerald-700 border-emerald-500/50'
                                      : application.status === 'rejected'
                                      ? 'bg-rose-600 hover:bg-rose-700 border-rose-500/50'
                                      : 'bg-blue-600 hover:bg-blue-700 border-blue-500/50'
                                    : 'bg-[#0091ca] hover:bg-[#007ba6] border-[#0091ca]/70'
                                } text-white font-bold text-[11px] uppercase px-5 py-2 rounded-lg tracking-wider flex items-center gap-2 select-none duration-150 active:scale-95 transition-all cursor-pointer shadow-xs border`}
                                id={`apply-button-${opp.id}`}
                              >
                                <span>{
                                  application
                                    ? application.status === 'accepted'
                                      ? 'Accepted / View'
                                      : application.status === 'rejected'
                                      ? 'Declined / View'
                                      : 'Applied / View'
                                    : 'View Details'
                                }</span>
                                <ArrowRight className="w-3.5 h-3.5 text-white" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleViewOpportunity(opp)}
                                className="bg-slate-800 hover:bg-slate-700 text-white font-bold text-[11px] uppercase px-5 py-2 rounded-lg tracking-widest flex items-center gap-2 select-none duration-150 active:scale-95 transition-all cursor-pointer shadow-xs"
                              >
                                <span>View Opportunity</span>
                                <ArrowRight className="w-3.5 h-3.5 text-white" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* Standalone Opportunity Detail View (Opens directly at the top) */}
        {activeTab === 'details' && selectedOpp && (
          <div
            key={`details-${selectedOpp.id}`}
            className="w-full bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm"
          >
            <div className="p-4 pl-8 pt-8">
              <button 
                onClick={handleBackToOpportunities}
                className="flex items-center gap-2 text-slate-500 hover:text-slate-900 font-bold uppercase tracking-widest text-xs cursor-pointer select-none"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Opportunities
              </button>
            </div>
            {/* Header */}
            <div className="bg-white p-8 flex items-start justify-between border-b border-slate-100">
              <div className="flex items-start gap-6">
                <div className="w-28 h-28 md:w-32 md:h-32 bg-white border border-slate-200 rounded-2xl p-1.5 flex items-center justify-center shadow-xs flex-shrink-0 mt-1 overflow-hidden">
                  {(() => {
                    const orgProfile = (users || []).find(u => u.uid === selectedOpp.organizationId);
                    const relatedTenant = (tenants || []).find(t => t.id === selectedOpp.tenantId);
                    const orgLogo = orgProfile?.avatar || selectedOpp.externalLogoUrl || relatedTenant?.logoUrl || relatedTenant?.avatar;
                    return getOrganizationLogo(selectedOpp.externalOrganizationName || selectedOpp.organizationName, orgLogo, selectedOpp.organizationId, selectedOpp.careerCluster || selectedOpp.industry);
                  })()}
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em] mb-1 flex flex-wrap items-center gap-1.5">
                    <span>PLACEMENT PARTNER // {selectedOpp.externalOrganizationName || selectedOpp.organizationName}</span>
                    {(() => {
                      const orgProfile = (users || []).find(u => u.uid === selectedOpp.organizationId);
                      if (orgProfile?.verified) {
                        return (
                          <span className="inline-flex items-center gap-0.5 text-[8px] font-black text-sky-700 bg-sky-50 px-1.5 py-0.2 border border-sky-100 rounded uppercase tracking-widest font-sans normal-case">
                            <Check className="w-2.5 h-2.5 text-sky-600" />
                            Verified Placement Partner
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </p>
                  <h1 className="text-3xl font-extrabold text-[#0f2942] tracking-tight flex flex-wrap items-center gap-3">
                    <span>{selectedOpp.title}</span>
                    {selectedOpp.status === 'archived' && (
                      <span className="text-[10px] font-black text-rose-700 bg-rose-50 px-3 py-1 rounded-full border border-rose-100 uppercase tracking-widest font-sans">
                        Archived / Deleted
                      </span>
                    )}
                    {selectedOpp.status === 'closed' && (
                      <span className="text-[10px] font-black text-amber-700 bg-amber-50 px-3 py-1 rounded-full border border-amber-100 uppercase tracking-widest font-sans">
                        Closed
                      </span>
                    )}
                  </h1>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[10px] font-bold text-[#0091ca] bg-[#eefaff] px-2.5 py-1 rounded-full border border-[#bae6fd] tracking-widest uppercase">VERIFIED</span>
                    <span className="text-[10px] font-bold text-[#475569] bg-slate-50 px-2.5 py-1 rounded-full border border-slate-200 tracking-widest uppercase">{selectedOpp.careerCluster || selectedOpp.industry || 'EDUCATION'}</span>
                  </div>
                  <div className="flex flex-col gap-y-2 mt-4 text-[11px] font-bold text-slate-500 font-mono">
                    <div className="flex items-center space-x-3">
                      <MapPin className="w-4 h-4 text-[#0091ca]" />
                      <span>{selectedOpp.location || selectedOpp.address}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span>{selectedOpp.totalHours || selectedOpp.hoursRequired} Hours Required</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <span className={selectedOpp.paymentStructure === 'Unpaid' ? 'text-slate-400' : 'text-emerald-700 font-bold'}>
                        {selectedOpp.paymentStructure === 'Unpaid' 
                          ? 'Unpaid / Credit Only' 
                          : `Paid: $${selectedOpp.hourlyRate || '0.00'} (${selectedOpp.paymentStructure || 'Hourly Wage'})`}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3 text-slate-400 italic">
                      <Calendar className="w-4 h-4 text-slate-300" />
                      <span>Posted: {formatOpportunityDate(selectedOpp.createdAt)}</span>
                    </div>
                    {selectedOpp.externalContactEmail && (
                      <div className="flex items-center space-x-3 text-[#0094c8]">
                        <Mail className="w-4 h-4 text-[#0094c8] shrink-0" />
                        <span className="text-slate-500 font-sans text-[11px] font-bold">Opportunity Contact:</span>
                        <a 
                          href={`mailto:${selectedOpp.externalContactEmail}`}
                          className="hover:underline font-mono text-xs font-semibold lowercase text-[#0094c8]"
                          title="Email Opportunity Contact"
                        >
                          {selectedOpp.externalContactEmail}
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">OPEN CAPACITY</p>
                <div className="flex items-center justify-end gap-2 mt-1">
                  <Users className="w-5 h-5 text-slate-400" />
                  <span className="text-lg font-extrabold text-[#011d3c]">{selectedOpp.numOpenings || 1} Openings</span>
                </div>
                {(() => {
                  const orgProfile = (users || []).find(u => u.uid === selectedOpp.organizationId);
                  const website = selectedOpp.website || orgProfile?.website;
                  const href = website 
                    ? (website.startsWith('http') ? website : `https://${website}`)
                    : `https://www.google.com/search?q=${encodeURIComponent(selectedOpp.externalOrganizationName || selectedOpp.organizationName || selectedOpp.title)}`;
                  return (
                    <a 
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-block bg-slate-100 hover:bg-slate-200 text-[#011d3c] text-xs font-bold uppercase tracking-widest px-6 py-3 rounded-lg transition-all text-center select-none"
                    >
                      VISIT WEBSITE
                    </a>
                  );
                })()}
              </div>
            </div>

            {/* Action Bar (Apply / Recommend / Edit) placed above Map & Overview for visibility */}
            <div className="px-8 pt-6 pb-2">
              {activeUser && (activeUser.uid === selectedOpp.organizationId || doesRecordBelongToOrg(selectedOpp, activeUser, users) || activeUser.role === 'regional_admin' || activeUser.role === 'superadmin') && (
                <div className="mb-3">
                  <button
                    onClick={() => handleEditInitiation(selectedOpp)}
                    className="w-full bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 transition-all border border-slate-300 cursor-pointer shadow-xs"
                  >
                    Edit Opportunity
                  </button>
                </div>
              )}
              {(() => {
                if (activeUser?.role === 'parent') {
                  if (myChildren.length === 0) {
                    return (
                      <div className="w-full bg-amber-50 border border-amber-200 text-amber-800 font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex flex-col items-center justify-center gap-1 shadow-sm">
                        <span>No Linked Children Found</span>
                        <span className="text-[10px] lowercase text-amber-600 font-normal">Please link a student to your account</span>
                      </div>
                    );
                  }

                  const activeChildId = selectedChildId || (myChildren[0] ? myChildren[0].uid : '');
                  const isRecommended = selectedOpp && activeChildId && (recommendations || []).some(
                    r => r.opportunityId === selectedOpp.id && r.studentId === activeChildId
                  );

                  return (
                    <div className="w-full space-y-3 bg-slate-50/80 p-4 border border-slate-100 rounded-xl shadow-xs">
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black uppercase tracking-wider text-slate-500 block">
                          Recommend
                        </label>
                        <select
                          value={activeChildId}
                          onChange={(e) => setSelectedChildId(e.target.value)}
                          className="w-full px-3.5 py-3 border border-slate-200 bg-white focus:bg-white focus:border-[#0091ca] rounded-xl text-xs outline-none font-bold uppercase cursor-pointer"
                        >
                          {myChildren.map(child => (
                            <option key={child.uid} value={child.uid}>
                              {child.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <button
                        onClick={async () => {
                          if (!activeChildId || isRecommended || !selectedOpp) return;
                          try {
                            await recommendStudentToOpportunity(activeChildId, selectedOpp.id);
                          } catch (err) {
                            console.error("Failed to recommend to child:", err);
                          }
                        }}
                        disabled={!activeChildId || isRecommended}
                        className={`w-full font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 transition-all shadow-md ${
                          isRecommended
                            ? 'bg-emerald-50 border border-emerald-200 text-emerald-800 cursor-not-allowed'
                            : 'bg-[#0091ca] hover:bg-[#007ba6] text-white cursor-pointer'
                        }`}
                      >
                        {isRecommended ? (
                          <>
                            Recommended <CheckCircle className="w-4 h-4 text-emerald-600" />
                          </>
                        ) : (
                          <>
                            Recommend <ArrowRight className="w-4 h-4" />
                          </>
                        )}
                      </button>
                    </div>
                  );
                }

                if (activeUser?.role === 'advisor') {
                  const eligibleAdvisees = advisorAdvisees.filter(student => 
                    getStudentEligibility(selectedOpp, student, dbDistricts, dbSchools).eligible
                  );
                  const isRestrictedOther = isOppRestrictedForOtherSchools(selectedOpp, activeUser, dbDistricts, dbSchools, allSchools);

                  if (eligibleAdvisees.length === 0) {
                    return (
                      <div className="space-y-3 w-full">
                        <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-4 text-[11px] font-medium text-amber-900 flex items-start gap-3">
                          <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold uppercase tracking-wider text-[10px] text-amber-800 mb-1">
                              {isRestrictedOther ? "Restricted Opportunity" : "Cannot Recommend Placement"}
                            </p>
                            <p className="text-[11px] text-amber-900/90 leading-relaxed">
                              {isRestrictedOther 
                                ? "This opportunity is restricted to schools other than yours. None of your assigned students can apply."
                                : "None of your assigned students can apply to this opportunity due to school, district, or age restrictions."}
                            </p>
                          </div>
                        </div>
                        <button
                          disabled
                          className="w-full bg-slate-100 text-slate-400 border border-slate-200 font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 cursor-not-allowed select-none"
                        >
                          Recommendation Unavailable <XCircle className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>
                    );
                  }

                  return (
                    <button
                      onClick={() => {
                        setSelectedStudentsForRecommend([]);
                        setRecommendModalOpen(true);
                      }}
                      className="w-full bg-[#0f2d4a] hover:bg-[#0a1f33] text-white font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer"
                    >
                      Recommend Placement <ArrowRight className="w-4 h-4" />
                    </button>
                  );
                }

                const hasApplied = selectedOpp && myApplications.some(a => a.opportunityId === selectedOpp.id);
                if (hasApplied) {
                  return (
                    <div className="w-full bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 shadow-sm">
                      Applied <CheckCircle className="w-4 h-4 text-emerald-600" />
                    </div>
                  );
                }
                const eligibility = getStudentEligibility(selectedOpp, activeUser, dbDistricts, dbSchools);
                if (!eligibility.eligible) {
                  return (
                    <div className="space-y-3 w-full">
                      <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-4 text-[11px] font-medium text-amber-900 flex items-start gap-3">
                        <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-bold uppercase tracking-wider text-[10px] text-amber-800 mb-1">Application Restrictions Active</p>
                          <ul className="list-disc pl-4 space-y-1">
                            {eligibility.reasons.map((reason, i) => (
                              <li key={i}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <button
                        disabled
                        className="w-full bg-slate-100 text-slate-400 border border-slate-200 font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 cursor-not-allowed select-none"
                      >
                        Placement Unavailable <XCircle className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                  );
                }
                return (
                  <button
                    onClick={() => setSelectedOppForApply(selectedOpp)}
                    className="w-full bg-[#0091ca] hover:bg-[#007ba6] text-white font-bold text-xs uppercase px-5 py-4 rounded-xl tracking-widest flex items-center justify-center gap-2 transition-all shadow-md cursor-pointer"
                  >
                    Apply to Placement <ArrowRight className="w-4 h-4" />
                  </button>
                );
              })()}
            </div>

            {/* Map Section */}
            {(selectedOpp.location || selectedOpp.address) && (
              <div className="px-8 pt-6 pb-8">
                <div className="text-sm font-bold text-[#011d3c] mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[#0091ca]" />
                  Location
                </div>
                <OpportunityMap address={selectedOpp.location || selectedOpp.address || ''} />
              </div>
            )}

            {/* Position Overview, etc. */}
            <div className="p-8 border-t border-slate-100 space-y-8">
               {(() => {
                 const sections = getOpportunitySections(selectedOpp);
                 const displaySections = [
                   { icon: Info, title: 'Position Overview', content: renderRichContent(sections.overview) },
                   ...(selectedOpp.learningObjective ? [{ icon: Sparkles, title: 'Learning Objective', content: renderRichContent(selectedOpp.learningObjective) }] : []),
                   { icon: CheckSquare, title: 'Key Responsibilities', content: renderRichContent(sections.responsibilities) },
                   { icon: CheckCircle, title: 'Qualifications', content: renderRichContent(sections.qualifications) },
                   { icon: Sparkles, title: 'Focus Skillsets', content: renderRichContent(sections.skills) },
                   {
                     icon: ShieldAlert,
                     title: 'Eligibility & Constraints',
                     content: (() => {
                       const hasDistricts = selectedOpp.targetSchoolDistricts && selectedOpp.targetSchoolDistricts.length > 0;
                       const hasSchools = selectedOpp.targetSchools && selectedOpp.targetSchools.length > 0;
                       const hasAgeMin = selectedOpp.minAge !== undefined && selectedOpp.minAge !== null;
                       const hasAgeMax = selectedOpp.maxAge !== undefined && selectedOpp.maxAge !== null;

                       if (!hasDistricts && !hasSchools && !hasAgeMin && !hasAgeMax) {
                         return <span className="text-slate-400 italic">Open to all applicants without school or age restrictions.</span>;
                       }

                       const distNames = (selectedOpp.targetSchoolDistricts || []).map(id => {
                         const dbD = dbDistricts.find(d => d.id === id);
                         return dbD ? dbD.name : id;
                       });

                       const schoolNames = (selectedOpp.targetSchools || []).map(id => {
                         const dbS = dbSchools.find(s => s.id === id);
                         return dbS ? dbS.name : id;
                       });

                       return (
                         <div className="space-y-3 mt-1.5 bg-white p-5 rounded-2xl border border-slate-200 shadow-xs max-w-lg">
                           {hasDistricts && (
                             <div>
                               <span className="font-bold text-[#011d3c] text-[10px] uppercase tracking-wider block mb-1">Eligible Districts</span>
                               <div className="flex flex-wrap gap-1.5">
                                 {distNames.map((name, i) => (
                                   <span key={i} className="text-[11px] font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2.5 py-0.5 rounded-md shadow-2xs">
                                     {name.toUpperCase()}
                                   </span>
                                 ))}
                               </div>
                             </div>
                           )}
                           {hasSchools && (
                             <div>
                               <span className="font-bold text-[#011d3c] text-[10px] uppercase tracking-wider block mb-1">Eligible Schools</span>
                               <div className="flex flex-wrap gap-1.5">
                                 {schoolNames.map((name, i) => (
                                   <span key={i} className="text-[11px] font-semibold text-slate-700 bg-slate-50 border border-slate-200 px-2.5 py-0.5 rounded-md shadow-2xs">
                                     {name.toUpperCase()}
                                   </span>
                                 ))}
                               </div>
                             </div>
                           )}
                           {(hasAgeMin || hasAgeMax) && (
                             <div>
                               <span className="font-bold text-[#011d3c] text-[10px] uppercase tracking-wider block mb-1">Age Requirement</span>
                               <span className="text-xs font-bold text-[#0094c8] bg-slate-50 border border-slate-200 px-3 py-1 rounded-md shadow-2xs inline-block">
                                 {hasAgeMin && hasAgeMax
                                   ? `Between ${selectedOpp.minAge} and ${selectedOpp.maxAge} years old`
                                   : hasAgeMin
                                   ? `At least ${selectedOpp.minAge} years old`
                                   : `Up to ${selectedOpp.maxAge} years old`
                                 }
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()
                    }
                  ];
                 return displaySections.map((sect) => (
                   <div key={sect.title} className="flex gap-4">
                     <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                       <sect.icon className="w-4 h-4 text-slate-600" />
                     </div>
                     <div className="flex-1">
                       <h3 className="text-sm font-bold text-[#011d3c] mb-2">{sect.title}</h3>
                       <div className="text-sm text-slate-600">{sect.content}</div>
                     </div>
                   </div>
                 ));
               })()}
            </div>
          </div>
        )}

        {/* Tab 2: Recreated Cinematic High-Fidelity Post Opportunity Screen */}
        {activeTab === 'post' && (
          <motion.div
            key="post"
            initial={{ opacity: 0, y: 70 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -40 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="w-full space-y-6"
          >
            {submissionSuccess ? (
              <div className="py-12 flex flex-col items-center justify-center space-y-3.5 text-center bg-white rounded-2xl border border-slate-150 shadow-sm animate-pulse">
                <div className="h-14 w-14 bg-emerald-50 border border-emerald-200 text-emerald-600 rounded-full flex items-center justify-center text-3xl shadow-sm font-bold">
                  ✓
                </div>
                <div>
                  <h3 className="text-base font-black text-slate-900">Posting Package Transmitted Successfully!</h3>
                  <p className="text-xs text-slate-500 mt-1">Replicated across your targeted regional settings within local systems.</p>
                </div>
                <div className="text-[11px] text-custom-primary font-mono select-none">
                  Returning to Vacancies Directory List...
                </div>
              </div>
            ) : (
              <form onSubmit={handleFullPostSubmission} className="space-y-6">
                
                {/* 1. Header (Posting Configuration / Toggle row) */}
                <div className="mt-8 flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 tracking-tight">Posting Configuration</h2>
                    <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase mt-0.5">
                      UNIFIED CLASSIFICATION AND LOGISTICS CONTROL.
                    </p>
                  </div>
                  
                  {/* Action Toggles on the right */}
                  <div className="flex flex-wrap items-center gap-4">
                    {/* VISIBLE TO STUDENTS toggle */}
                    <div className="flex items-center space-x-2.5">
                      <span className="text-[10px] font-bold text-slate-700 tracking-wider">VISIBLE</span>
                      <button
                        type="button"
                        onClick={() => setIsVisible(!isVisible)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          isVisible ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}
                        id="toggle-visible"
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                            isVisible ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* OPEN status toggle */}
                    <div className="flex items-center space-x-2.5">
                      <span className="text-[10px] font-bold text-slate-700 tracking-wider">OPEN</span>
                      <button
                        type="button"
                        onClick={() => setIsOpenState(!isOpenState)}
                        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          isOpenState ? 'bg-emerald-500' : 'bg-slate-300'
                        }`}
                        id="toggle-open"
                      >
                        <span
                          className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200 ease-in-out ${
                            isOpenState ? 'translate-x-4' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>

                    {/* Return back button */}
                    <button
                      type="button"
                      onClick={() => setActiveTab('all')}
                      className="flex items-center space-x-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-lg border border-slate-200 transition-all text-[11px] cursor-pointer"
                    >
                      <ArrowLeft className="w-3.5 h-3.5 text-slate-500" />
                      <span>Back</span>
                    </button>

                    {editingOppId && (
                      <button
                        type="button"
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center space-x-1 px-3 py-1.5 bg-rose-50 hover:bg-rose-105 text-rose-700 font-bold rounded-lg border border-rose-200 hover:border-rose-300 transition-all text-[11px] cursor-pointer shadow-xs"
                      >
                        <Trash2 className="w-3.5 h-3.5 text-rose-600 animate-pulse" />
                        <span>Delete Posting</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* POSTING IDENTITY SELECTION (Superadmin / Regional Admin Step) */}
                {activeUser && (activeUser.role === 'superadmin' || activeUser.role === 'regional_admin') && (
                  <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-5">
                    <div className="font-semibold text-slate-700 text-[10px] uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-100 pb-3">
                      <Users className="w-4 h-4 text-[#0094c8]" />
                      <span className="text-slate-800 font-extrabold text-[10px] tracking-wide">POSTING IDENTITY & PARTNER CONTROL</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Option 1: Existing Portal Organization */}
                      <button
                        type="button"
                        onClick={() => setPostAsType('organization')}
                        className={`p-4 rounded-xl border text-left transition-all flex items-start space-x-3 cursor-pointer ${
                          postAsType === 'organization'
                            ? 'bg-sky-50/40 border-[#0094c8] shadow-xs'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                          postAsType === 'organization' ? 'border-[#0094c8] bg-white' : 'border-slate-300'
                        }`}>
                          {postAsType === 'organization' && <div className="w-2 h-2 rounded-full bg-[#0094c8]" />}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800">Post as Registered Organization</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Publish on behalf of an existing employer partner active in the system.</p>
                        </div>
                      </button>

                      {/* Option 2: External Partner */}
                      <button
                        type="button"
                        onClick={() => setPostAsType('external')}
                        className={`p-4 rounded-xl border text-left transition-all flex items-start space-x-3 cursor-pointer ${
                          postAsType === 'external'
                            ? 'bg-sky-50/40 border-[#0094c8] shadow-xs'
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        <div className={`mt-0.5 w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                          postAsType === 'external' ? 'border-[#0094c8] bg-white' : 'border-slate-300'
                        }`}>
                          {postAsType === 'external' && <div className="w-2 h-2 rounded-full bg-[#0094c8]" />}
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800">Post as External Partner</p>
                          <p className="text-[10px] text-slate-500 mt-0.5">Publish as a one-off external company with a custom name and logo upload.</p>
                        </div>
                      </button>
                    </div>

                    {/* Conditional inputs */}
                    {postAsType === 'organization' ? (
                      <div className="space-y-1.5 animate-fade-in pt-1">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">SELECT REGISTERED PORTAL ORGANIZATION</label>
                        <select
                          required={postAsType === 'organization'}
                          value={selectedOrgId}
                          onChange={(e) => setSelectedOrgId(e.target.value)}
                          className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs"
                        >
                          <option value="" disabled>Choose an Organization...</option>
                          {existingOrgs.map(org => (
                            <option key={org.uid} value={org.uid}>
                              {org.orgName || org.name} ({org.email})
                            </option>
                          ))}
                        </select>
                        {existingOrgs.length === 0 && (
                          <p className="text-[10px] text-amber-600 font-medium">No registered organization profiles found. Please register some organizations or use the External Partner option.</p>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4 animate-fade-in pt-1">
                        {/* Name field */}
                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">EXTERNAL PARTNER NAME</label>
                          <input
                            type="text"
                            required={postAsType === 'external'}
                            placeholder="e.g. Google Cloud Services, Acme Corp"
                            value={externalPartnerName}
                            onChange={(e) => setExternalPartnerName(e.target.value)}
                            className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-800 font-medium text-xs placeholder:text-slate-400"
                          />
                        </div>

                        {/* Contact Email field */}
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                              EXTERNAL PARTNER CONTACT EMAIL
                            </label>
                            <span className="text-[9px] font-bold text-[#0094c8] uppercase tracking-wider">
                              Opportunity Contact
                            </span>
                          </div>
                          <div className="relative">
                            <input
                              type="email"
                              required={postAsType === 'external'}
                              placeholder="e.g. contact@company.com, hiring@partner.org"
                              value={externalPartnerEmail}
                              onChange={(e) => setExternalPartnerEmail(e.target.value)}
                              className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 pl-10 focus:ring-1 focus:ring-[#0094c8] text-slate-800 font-medium text-xs placeholder:text-slate-400"
                            />
                            <Mail className="w-4 h-4 text-slate-400 absolute left-3.25 top-3.25" />
                          </div>
                          <p className="text-[10px] text-slate-500 font-normal">
                            This contact is treated the same as all opportunity contacts: they will receive email confirmations about the posting, student applications, candidate responses, and interview updates.
                          </p>
                        </div>

                        {/* Logo Upload area */}
                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">EXTERNAL PARTNER LOGO (UPLOAD)</label>
                          <div className="flex flex-col sm:flex-row items-center gap-4 bg-slate-50/50 border border-dashed border-slate-200 p-4 rounded-xl">
                            {/* Preview box */}
                            <div className="w-32 h-32 rounded-2xl border border-slate-300 bg-white flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                              {externalPartnerLogoUrl ? (
                                <img
                                  src={externalPartnerLogoUrl}
                                  alt="Preview"
                                  className="w-full h-full object-cover animate-fade-in"
                                  referrerPolicy="no-referrer"
                                />
                              ) : (
                                <Building className="w-12 h-12 text-slate-300 animate-pulse" />
                              )}
                            </div>

                            {/* Controls */}
                            <div className="flex-1 space-y-1.5 text-center sm:text-left w-full">
                              <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
                                <label className="cursor-pointer px-4 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold text-[10px] uppercase tracking-wider rounded-lg shadow-2xs transition-all flex items-center space-x-1.5">
                                  <Upload className="w-3.5 h-3.5 text-slate-500" />
                                  <span>Choose Logo File</span>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoUploadChange}
                                    className="hidden"
                                  />
                                </label>
                                {externalPartnerLogoUrl && (
                                  <button
                                    type="button"
                                    onClick={() => setExternalPartnerLogoUrl('')}
                                    className="px-3 py-2 bg-rose-50 hover:bg-rose-100 text-rose-600 font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all flex items-center space-x-1"
                                  >
                                    <X className="w-3 h-3" />
                                    <span>Clear Logo</span>
                                  </button>
                                )}
                              </div>
                              <p className="text-[10px] text-slate-400">PNG or JPG formats supported. Maximum size 5MB.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* 2. REGIONAL ROUTING Container */}
                <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-3">
                  <div className="font-semibold text-slate-700 text-[10px] uppercase tracking-wider flex items-center space-x-1.5">
                    <Globe className="w-4 h-4 text-[#0094c8]" />
                    <span className="text-sky-600 font-bold tracking-wide">REGIONAL ROUTING</span>
                  </div>

                  <div className="relative">
                    {/* Collapsible Header style trigger to match the image select box */}
                    <button
                      type="button"
                      onClick={() => setShowRegionsDropdown(!showRegionsDropdown)}
                      className="w-full flex items-center justify-between p-3 rounded-lg bg-[#f4f6f8] border border-slate-200 hover:bg-slate-100 transition-all text-slate-800 font-bold text-xs cursor-pointer placeholder:text-slate-400"
                    >
                      <div className="flex items-center space-x-2 font-bold text-xs tracking-wider text-slate-700">
                        <span>{selectedRegions.length} REGIONS SELECTED</span>
                      </div>
                      {showRegionsDropdown ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                    </button>

                    {/* Regional Select checkboxes inside collapsed list */}
                    {showRegionsDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg p-3 shadow-lg max-h-60 overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-2 animate-in fade-in slide-in-from-top-1">
                        {allowedRegions.map(t => {
                          const isSelected = selectedRegions.includes(t.id);
                          return (
                            <label
                              key={t.id}
                              className={`w-full flex items-center justify-between p-2 rounded-lg border text-left text-xs transition-all cursor-pointer ${
                                isSelected
                                  ? 'bg-[#0094c8]/5 border-[#0094c8]/40 text-[#0094c8] font-bold'
                                  : 'bg-white border-slate-200 text-slate-650 hover:bg-slate-50'
                              }`}
                            >
                              <span>{t.name}</span>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => {
                                  if (isSelected) {
                                    if (selectedRegions.length > 1) {
                                      setSelectedRegions(selectedRegions.filter(id => id !== t.id));
                                    }
                                  } else {
                                    setSelectedRegions([...selectedRegions, t.id]);
                                    const config = getRegionConstraints(t.id, t.name);
                                    setSelectedDistricts(prev => [...prev, ...config.districts]);
                                    setSelectedInstitutions(prev => [...prev, ...config.schools]);
                                  }
                                }}
                                className="w-3.5 h-3.5 accent-[#0094c8]"
                              />
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <p className="text-[10px] text-slate-400 font-mono italic tracking-wide uppercase">
                    THIS POSTING WILL BE REPLICATED ACROSS ALL SELECTED REGIONAL TENANTS.
                  </p>
                </div>

                {/* 3. PLACEMENT DETAILS (Matching Image 1 fields layout) */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-5">
                  
                  {/* SMART SEARCH FOR OCCUPATIONS */}
                  <div className="bg-slate-50/50 border border-slate-200 p-5 rounded-xl space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                      <div className="flex items-center space-x-2">
                        <Sparkles className="w-4 h-4 text-[#0094c8] animate-pulse" />
                        <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">Smart Search Occupations</span>
                      </div>
                      <span className="text-[10px] text-slate-400 font-mono">
                        {uploadedCareers.length} REPOSITORY RECORDS
                      </span>
                    </div>
                    
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      Search from pre-loaded occupations in the Superadmin console. Click on an occupation to select its Cluster and Sub Cluster.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Text Search */}
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search occupation or code..."
                          value={smartSearchQuery}
                          onChange={(e) => setSmartSearchQuery(e.target.value)}
                          className="w-full bg-white border border-slate-200 outline-none rounded-lg py-2 pl-9 pr-3 text-xs text-slate-700 placeholder:text-slate-400 focus:ring-1 focus:ring-[#0094c8]"
                        />
                      </div>

                      {/* Filter Cluster */}
                      <div>
                        <select
                          value={smartSearchCluster}
                          onChange={(e) => {
                            setSmartSearchCluster(e.target.value);
                            setSmartSearchSubcluster(''); // Reset subcluster on cluster change
                          }}
                          className="w-full bg-white border border-slate-200 outline-none rounded-lg p-2 text-xs text-slate-700 focus:ring-1 focus:ring-[#0094c8] cursor-pointer"
                        >
                          <option value="">All Career Clusters</option>
                          {smartClusterOptions.map(cluster => (
                            <option key={cluster} value={cluster}>{cluster}</option>
                          ))}
                        </select>
                      </div>

                      {/* Filter Subcluster */}
                      <div>
                        <select
                          value={smartSearchSubcluster}
                          onChange={(e) => setSmartSearchSubcluster(e.target.value)}
                          className="w-full bg-white border border-slate-200 outline-none rounded-lg p-2 text-xs text-slate-700 focus:ring-1 focus:ring-[#0094c8] cursor-pointer"
                        >
                          <option value="">All Sub Clusters</option>
                          {smartSubclusterOptions.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Search Results List */}
                    <div className="bg-white border border-slate-200 rounded-xl max-h-48 overflow-y-auto divide-y divide-slate-100">
                      {loadingCareers ? (
                        <div className="p-4 text-center text-xs text-slate-400">
                          Loading occupations...
                        </div>
                      ) : filteredSmartCareers.length === 0 ? (
                        <div className="p-4 text-center text-xs text-slate-400 italic">
                          No matching occupations found in repository.
                        </div>
                      ) : (
                        groupedSmartCareers.map(group => (
                          <div key={group.cluster} className="divide-y divide-slate-100">
                            <div className="sticky top-0 bg-slate-50 border-b border-slate-200 px-3 py-1.5 z-10 text-[10px] font-bold text-slate-500 uppercase tracking-wider shadow-sm">
                              {group.cluster}
                            </div>
                            {group.occupations.map(career => (
                              <div
                                key={career.id}
                                onClick={() => {
                                  setFormTitle(career.occupation);
                                  setSelectedOccupationForSetup(career);
                                  setShowAppliedAlert(false);
                                }}
                                className={`p-3 text-left transition-all cursor-pointer flex items-center justify-between hover:bg-sky-50/40 ${
                                  selectedOccupationForSetup?.id === career.id ? 'bg-sky-50/60' : ''
                                }`}
                              >
                                <div>
                                  <div className="text-xs font-bold text-slate-800">{career.occupation}</div>
                                  <div className="text-[10px] text-slate-400 flex items-center space-x-1.5 mt-0.5">
                                    {career.code && <span className="font-mono bg-slate-100 px-1 rounded">{career.code}</span>}
                                    <span className="opacity-75">{career.cluster}</span>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="text-[10px] font-bold text-[#0094c8] hover:underline"
                                >
                                  Select
                                </button>
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </div>

                    {/* Occupation Details and Cluster / Sub-cluster setup area */}
                    {selectedOccupationForSetup && (
                      <div className="bg-sky-50/50 border border-[#bae6fd] p-4 rounded-xl space-y-3 animate-fade-in">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-xs font-extrabold text-[#0369a1]">
                              Occupation Selected: {selectedOccupationForSetup.occupation}
                            </h4>
                            <p className="text-[10px] text-slate-500 mt-0.5">
                              Cluster: <strong className="text-slate-700">{selectedOccupationForSetup.cluster}</strong>
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedOccupationForSetup(null)}
                            className="text-slate-400 hover:text-slate-600 text-xs font-bold"
                          >
                            ✕
                          </button>
                        </div>

                        <div className="space-y-1.5">
                          <span className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                            Select from attached Sub Clusters to apply:
                          </span>
                          <div className="flex flex-wrap gap-2">
                            {selectedOccupationForSetup.subclusters && selectedOccupationForSetup.subclusters.length > 0 ? (
                              selectedOccupationForSetup.subclusters.map((sub, i) => (
                                <button
                                  key={`${sub}-${i}`}
                                  type="button"
                                  onClick={() => {
                                    setCareerCluster(selectedOccupationForSetup.cluster);
                                    setFunctionalFocus(sub);
                                    setOccupationSelection(selectedOccupationForSetup.occupation);
                                    setSelectedOccupationForSetup(null);
                                    setShowAppliedAlert(true);
                                    setTimeout(() => setShowAppliedAlert(false), 4000);
                                  }}
                                  className="px-2.5 py-1.5 bg-white border border-[#bae6fd] hover:border-[#0094c8] hover:bg-sky-50 text-[11px] font-bold text-slate-700 rounded-lg transition-all shadow-xs cursor-pointer"
                                >
                                  {sub}
                                </button>
                              ))
                            ) : (
                              <p className="text-[10px] text-slate-400 italic">No sub-clusters attached. You can manually assign one below.</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {showAppliedAlert && (
                      <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 p-2.5 rounded-lg text-xs font-bold flex items-center space-x-2 animate-fade-in">
                        <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
                        <span>Occupation details and corresponding Cluster & Sub Cluster successfully applied below!</span>
                      </div>
                    )}
                  </div>

                  {/* PLACEMENT TITLE */}
                  <div className="space-y-1">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">PLACEMENT TITLE</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g., Marketing Intern"
                      value={formTitle}
                      onChange={(e) => setFormTitle(e.target.value)}
                      className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-800 font-medium text-xs placeholder:text-slate-405"
                    />
                  </div>

                  {/* CAREER CLUSTER, SUB CLUSTER & OCCUPATION */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">CAREER CLUSTER</label>
                      <select
                        value={careerCluster}
                        onChange={(e) => handleClusterChange(e.target.value)}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs cursor-pointer"
                      >
                        <option value="" disabled>Select Cluster...</option>
                        {Object.keys(subclustersMap).map(clusterKey => (
                          <option key={clusterKey} value={clusterKey}>{clusterKey}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">SUB CLUSTER</label>
                      <select
                        value={functionalFocus}
                        onChange={(e) => { setFunctionalFocus(e.target.value); setOccupationSelection(''); }}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs cursor-pointer"
                      >
                        <option value="">Select Sub Cluster (Optional)</option>
                        <option value="General">General</option>
                        {(subclustersMap[careerCluster] || []).map((f, idx) => (
                          <option key={`${f}-${idx}`} value={f}>{f}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">OCCUPATION</label>
                      <select
                        value={occupationSelection}
                        onChange={(e) => setOccupationSelection(e.target.value)}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs cursor-pointer"
                      >
                        <option value="">Select Occupation (Optional)</option>
                        {availableOccupations.map((occ, idx) => (
                          <option key={`occ-${idx}`} value={occ}>{occ}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* WORKSITE ADDRESS, SEMESTER, REGISTRY DEADLINE */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">WORKSITE ADDRESS</label>
                      <VerifiedAddressInput
                        id="opportunity-worksite-address"
                        value={worksiteAddress}
                        onChange={(val) => setWorksiteAddress(val)}
                        placeholder="e.g. 123 Main St, City, State ZIP"
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-805 text-xs font-medium placeholder:text-slate-405"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">SEMESTER</label>
                      <select
                        value={semester}
                        onChange={(e) => setSemester(e.target.value)}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs"
                      >
                        {(systemConfig?.availableTerms || ['Summer 2026', 'Fall 2026', 'Spring 2027', 'Summer 2027']).map(term => (
                          <option key={term} value={term}>{term}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">REGISTRY DEADLINE</label>
                      <div className="relative">
                        <input
                          type="date"
                          required
                          value={registryDeadline}
                          onChange={(e) => setRegistryDeadline(e.target.value)}
                          className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 pr-10 focus:ring-1 focus:ring-[#0094c8] text-slate-805 text-xs font-medium"
                        />
                        <Calendar className="absolute right-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      </div>
                    </div>
                  </div>

                  {/* TARGET LEVEL, OPENINGS, TOTAL HOURS */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">TARGET LEVEL</label>
                      <select
                        value={targetLevel}
                        onChange={(e) => setTargetLevel(e.target.value)}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs"
                      >
                        <option value="High School">High School</option>
                        <option value="Post-Secondary">Post-Secondary</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">OPENINGS</label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        required
                        value={openingsCount}
                        onChange={(e) => setOpeningsCount(Number(e.target.value))}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-900 text-xs font-medium"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">TOTAL HOURS</label>
                      <div className="relative">
                        <Clock className="absolute left-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          type="number"
                          min={1}
                          max={1000}
                          required
                          placeholder="e.g. 120"
                          value={totalHours}
                          onChange={(e) => setTotalHours(Number(e.target.value))}
                          className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 pl-10 focus:ring-1 focus:ring-[#0094c8] text-slate-900 text-xs font-mono font-medium placeholder:text-slate-405"
                        />
                      </div>
                    </div>
                  </div>

                  {/* PRIMARY SUPERVISOR & SUPERVISOR TITLE */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">PRIMARY SUPERVISOR</label>
                      <div className="relative">
                        <User className="absolute left-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          type="text"
                          required
                          placeholder="Name"
                          value={supervisorName}
                          onChange={(e) => setSupervisorName(e.target.value)}
                          className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 pl-10 focus:ring-1 focus:ring-[#0094c8] text-slate-800 text-xs font-medium placeholder:text-slate-405"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">SUPERVISOR TITLE</label>
                      <div className="relative">
                        <Briefcase className="absolute left-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          type="text"
                          required
                          placeholder="e.g. Director of Ops"
                          value={supervisorTitle}
                          onChange={(e) => setSupervisorTitle(e.target.value)}
                          className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 pl-10 focus:ring-1 focus:ring-[#0094c8] text-slate-805 text-xs font-medium placeholder:text-slate-405"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* OPPORTUNITY BANNER & IMAGE SELECTION */}
                {(() => {
                  return (
                    <div className="bg-white p-5 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">
                      <div className="font-semibold text-slate-700 text-[10px] uppercase tracking-wider flex items-center justify-between border-b border-slate-100 pb-2.5">
                        <div className="flex items-center space-x-1.5">
                          <ImageIcon className="w-4 h-4 text-[#0094c8]" />
                          <span className="text-slate-800 font-extrabold text-[10px] tracking-wide">OPPORTUNITY IMAGE</span>
                        </div>
                        <span className="text-[10px] font-mono font-bold text-slate-500 bg-slate-100 px-2.5 py-0.5 rounded-full uppercase">
                          {!careerCluster && imageSelectionMode === 'standard'
                            ? 'No Cluster Selected'
                            : imageSelectionMode === 'standard'
                            ? `Preset ${selectedStandardIndex + 1}`
                            : 'Custom Image'}
                        </span>
                      </div>

                      <p className="text-xs text-slate-500">
                        {!careerCluster
                          ? 'Select a Career Cluster above to view standard image options, or click the custom card to upload your own.'
                          : 'Select a pathway image thumbnail from superadmin presets or click the custom card to upload your own.'}
                      </p>

                      {/* THUMBNAILS ROW: AT MOST 3 SUPERADMIN THUMBNAILS + 1 BLANK UPLOAD THUMBNAIL */}
                      <div className="flex flex-wrap items-center gap-3 pt-1">
                        {!careerCluster ? (
                          <div className="flex items-center space-x-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-[11px] font-medium text-slate-500 italic">
                            <Info className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span>Select a Career Cluster above to see standard presets</span>
                          </div>
                        ) : (
                          /* Superadmin Console Thumbnail Options (At most 3, minimum 1) */
                          availableSuperadminImages.map((imgUrl, idx) => {
                            const isSelected = imageSelectionMode === 'standard' && selectedStandardIndex === idx;
                            return (
                              <button
                                key={`sa-img-${idx}`}
                                type="button"
                                onClick={() => {
                                  setImageSelectionMode('standard');
                                  setSelectedStandardIndex(idx);
                                }}
                                className={`relative w-24 h-16 sm:w-28 sm:h-18 rounded-xl overflow-hidden border-2 transition-all cursor-pointer group ${
                                  isSelected
                                    ? 'border-[#0094c8] ring-2 ring-[#0094c8]/20 shadow-sm scale-102'
                                    : 'border-slate-200 hover:border-slate-300 opacity-80 hover:opacity-100'
                                }`}
                              >
                                <img
                                  src={imgUrl}
                                  alt={`Option ${idx + 1}`}
                                  className="w-full h-full object-cover"
                                  referrerPolicy="no-referrer"
                                />
                                {isSelected && (
                                  <div className="absolute top-1 right-1 bg-[#0094c8] text-white p-0.5 rounded-full shadow-xs">
                                    <Check className="w-3 h-3" />
                                  </div>
                                )}
                                <div className="absolute bottom-0 inset-x-0 bg-black/60 backdrop-blur-xs px-1 py-0.5 text-center">
                                  <span className="text-[9px] font-bold text-white block truncate">
                                    Option {idx + 1}
                                  </span>
                                </div>
                              </button>
                            );
                          })
                        )}

                        {/* BLANK / CUSTOM UPLOAD THUMBNAIL OPTION */}
                        {(() => {
                          const isCustomSelected = imageSelectionMode === 'custom';
                          return (
                            <button
                              type="button"
                              onClick={() => {
                                setImageSelectionMode('custom');
                              }}
                              className={`relative w-24 h-16 sm:w-28 sm:h-18 rounded-xl overflow-hidden border-2 transition-all cursor-pointer group flex flex-col items-center justify-center ${
                                isCustomSelected
                                  ? 'border-[#0094c8] ring-2 ring-[#0094c8]/20 shadow-sm bg-sky-50/60'
                                  : 'border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400 opacity-90'
                              }`}
                            >
                              {customImageUrl ? (
                                <>
                                  <img
                                    src={customImageUrl}
                                    alt="Custom Upload"
                                    className="w-full h-full object-cover"
                                    referrerPolicy="no-referrer"
                                  />
                                  {isCustomSelected && (
                                    <div className="absolute top-1 right-1 bg-[#0094c8] text-white p-0.5 rounded-full shadow-xs">
                                      <Check className="w-3 h-3" />
                                    </div>
                                  )}
                                  <div className="absolute bottom-0 inset-x-0 bg-black/60 backdrop-blur-xs px-1 py-0.5 text-center">
                                    <span className="text-[9px] font-bold text-white block truncate">Custom</span>
                                  </div>
                                </>
                              ) : (
                                <div className="flex flex-col items-center justify-center p-1 space-y-1 text-center">
                                  <div className="w-6 h-6 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[#0094c8] shadow-2xs group-hover:scale-110 transition-transform">
                                    <Upload className="w-3.5 h-3.5" />
                                  </div>
                                  <span className="text-[9px] font-extrabold text-slate-600 uppercase tracking-tight leading-none">
                                    Upload Custom
                                  </span>
                                </div>
                              )}
                            </button>
                          );
                        })()}
                      </div>

                      {/* CUSTOM IMAGE CONTROLS (Only visible when Custom Upload thumbnail is active) */}
                      {imageSelectionMode === 'custom' && (
                        <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/70 space-y-3 mt-2">
                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={handleOppImageUploadChange}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            />
                            <div className="w-full bg-white border border-slate-200 hover:border-[#0094c8] rounded-lg px-4 py-2.5 flex items-center justify-center space-x-2 text-xs font-bold text-slate-700 shadow-2xs cursor-pointer hover:bg-sky-50/40 transition-colors">
                              <Upload className="w-4 h-4 text-[#0094c8]" />
                              <span>{customImageUrl ? 'Change Uploaded Image File...' : 'Choose Image File...'}</span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 4. COMPENSATION Card Section */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-5">
                  <div className="font-semibold text-slate-700 text-[10px] uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-100 pb-3">
                    <DollarSign className="w-4 h-4 text-[#0094c8] animate-pulse" />
                    <span className="text-slate-800 font-extrabold text-[10px] tracking-wide">COMPENSATION</span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-6 pt-1">
                    {/* PAID Radio button wrapper */}
                    <button
                      type="button"
                      onClick={() => { setCompensationType('paid'); setPayStructure('Hourly Wage'); }}
                      className="flex items-center space-x-2.5 cursor-pointer text-left py-1.5 px-3 rounded-lg hover:bg-slate-50 transition-all"
                    >
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                        compensationType === 'paid' ? 'border-[#0094c8] bg-white' : 'border-slate-300'
                      }`}>
                        {compensationType === 'paid' && <div className="w-2 h-2 rounded-full bg-[#0094c8]" />}
                      </div>
                      <span className="text-xs font-bold text-slate-800">PAID ROLE</span>
                    </button>

                    {/* UNPAID Radio button wrapper */}
                    <button
                      type="button"
                      onClick={() => { setCompensationType('unpaid'); setPayStructure('Unpaid'); setPayAmount('0.00'); }}
                      className="flex items-center space-x-2.5 cursor-pointer text-left py-1.5 px-3 rounded-lg hover:bg-slate-50 transition-all"
                    >
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${
                        compensationType === 'unpaid' ? 'border-[#0094c8] bg-white' : 'border-slate-300'
                      }`}>
                        {compensationType === 'unpaid' && <div className="w-2 h-2 rounded-full bg-[#0094c8]" />}
                      </div>
                      <span className="text-xs font-bold text-slate-800">UNPAID / CREDIT ONLY</span>
                    </button>
                  </div>

                  {/* Pay Structure & Pay Amount inline */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-1">
                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">PAY STRUCTURE</label>
                      <select
                        value={payStructure}
                        disabled={compensationType === 'unpaid'}
                        onChange={(e) => setPayStructure(e.target.value)}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-[#1e293b] font-bold text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="Hourly Wage">Hourly Wage</option>
                        <option value="Stipend Matrix">Fixed Placement Stipend</option>
                        <option value="Unpaid">Unpaid / Course Accrual Only</option>
                      </select>
                    </div>

                    <div className="space-y-1">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">AMOUNT ($)</label>
                      <input
                        type="text"
                        required
                        disabled={compensationType === 'unpaid'}
                        placeholder="e.g., 15.00"
                        value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-850 text-xs font-mono font-bold placeholder:text-slate-405 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* 5. DISTRICT, SCHOOL & AGE CONSTRAINTS */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-5">
                  <div className="font-semibold text-slate-700 text-[10px] uppercase tracking-wider flex items-center space-x-1.5 border-b border-slate-100 pb-3">
                    <Filter className="w-4 h-4 text-[#0094c8]" />
                    <span className="text-slate-800 font-extrabold text-[10px] tracking-wide">REQUIRED CONSTRAINTS</span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    
                    {/* DISTRICT CONSTRAINT BAR */}
                    <div className="space-y-2">
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">DISTRICT CONSTRAINT</label>
                      <div className="bg-[#f4f6f8] p-4 rounded-xl border border-slate-200 shadow-xs space-y-3 max-h-60 overflow-y-auto">
                        {availableDistricts.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Select at least one region above to populate districts.</p>
                        ) : (
                          <>
                            <label
                              className={`flex items-center space-x-2.5 cursor-pointer text-left w-full hover:bg-slate-100/50 transition-all p-1 rounded ${selectedDistricts.length === availableDistricts.length && availableDistricts.length > 0 ? 'bg-[#0094c8]/5' : 'bg-transparent'}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedDistricts.length === availableDistricts.length && availableDistricts.length > 0}
                                onChange={() => setSelectedDistricts(prev => prev.length === availableDistricts.length && availableDistricts.length > 0 ? [] : availableDistricts.map(d => d.id))}
                                className="w-4 h-4 rounded border-slate-350 text-[#0094c8] accent-[#0094c8]"
                              />
                              <span className={`text-xs font-bold ${selectedDistricts.length === availableDistricts.length && availableDistricts.length > 0 ? 'text-[#0094c8]' : 'text-slate-750'}`}>SELECT ALL</span>
                            </label>
                            {availableDistricts.map((dist, idx) => {
                              const isSel = selectedDistricts.includes(dist.id);
                              return (
                                <label
                                  key={`${dist.id}-${idx}`}
                                  className={`flex items-center space-x-2.5 cursor-pointer text-left w-full hover:bg-slate-100/50 transition-all p-1 rounded ${isSel ? 'bg-[#0094c8]/5' : 'bg-transparent'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSel}
                                    onChange={() => setSelectedDistricts(prev => prev.includes(dist.id) ? prev.filter(id => id !== dist.id) : [...prev, dist.id])}
                                    className="w-4 h-4 rounded border-slate-350 text-[#0094c8] accent-[#0094c8]"
                                  />
                                  <span className={`text-xs font-bold ${isSel ? 'text-[#0094c8]' : 'text-slate-750'}`}>{dist.name.toUpperCase()}</span>
                                </label>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>

                    {/* SCHOOL CONSTRAINT BAR */}
                    <div className="space-y-2">
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">SCHOOL CONSTRAINT</label>
                      <div className="bg-[#f4f6f8] p-4 rounded-xl border border-slate-200 shadow-xs space-y-3 max-h-60 overflow-y-auto">
                        {availableSchools.length === 0 ? (
                          <p className="text-xs text-slate-400 italic">Select at least one region or district above to populate schools.</p>
                        ) : (
                          <>
                            <label
                              className={`flex items-center space-x-2.5 cursor-pointer text-left w-full hover:bg-slate-100/50 transition-all p-1 rounded ${selectedInstitutions.length === availableSchools.length && availableSchools.length > 0 ? 'bg-[#0094c8]/5' : 'bg-transparent'}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedInstitutions.length === availableSchools.length && availableSchools.length > 0}
                                onChange={() => setSelectedInstitutions(prev => prev.length === availableSchools.length && availableSchools.length > 0 ? [] : availableSchools.map(s => s.id))}
                                className="w-4 h-4 rounded border-slate-350 text-[#0094c8] accent-[#0094c8]"
                              />
                              <span className={`text-xs font-bold ${selectedInstitutions.length === availableSchools.length && availableSchools.length > 0 ? 'text-[#0094c8]' : 'text-slate-750'}`}>SELECT ALL</span>
                            </label>
                            {availableSchools.map((school, idx) => {
                              const isSel = selectedInstitutions.includes(school.id);
                              return (
                                <label
                                  key={`${school.id}-${idx}`}
                                  className={`flex items-center space-x-2.5 cursor-pointer text-left w-full hover:bg-slate-100/50 transition-all p-1 rounded ${isSel ? 'bg-[#0094c8]/5' : 'bg-transparent'}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSel}
                                    onChange={() => setSelectedInstitutions(prev => prev.includes(school.id) ? prev.filter(id => id !== school.id) : [...prev, school.id])}
                                    className="w-4 h-4 rounded border-slate-350 text-[#0094c8] accent-[#0094c8]"
                                  />
                                  <span className={`text-xs font-bold ${isSel ? 'text-[#0094c8]' : 'text-slate-750'}`}>{school.name.toUpperCase()}</span>
                                </label>
                              );
                            })}
                          </>
                        )}
                      </div>
                    </div>

                    {/* AGE CONSTRAINT SECTION */}
                    <div className="col-span-1 md:col-span-2 space-y-2 pt-2">
                      <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">
                        AGE CONSTRAINT (YEARS)
                      </label>
                      <div className="bg-[#f4f6f8] p-5 rounded-xl border border-slate-200 shadow-xs grid grid-cols-1 sm:grid-cols-2 gap-5 mt-1">
                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">
                            MINIMUM AGE
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="120"
                            placeholder="e.g., 16"
                            value={minAge}
                            onChange={(e) => setMinAge(e.target.value)}
                            className="w-full bg-white border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-850 text-xs font-bold"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="block text-[10px] font-bold text-slate-550 uppercase tracking-wider">
                            MAXIMUM AGE
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="120"
                            placeholder="e.g., 19"
                            value={maxAge}
                            onChange={(e) => setMaxAge(e.target.value)}
                            className="w-full bg-white border border-slate-200 outline-none rounded-lg p-3 focus:ring-1 focus:ring-[#0094c8] text-slate-850 text-xs font-bold"
                          />
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* 6. Oslo AI CONTENT ENGINE */}
                <div className="bg-gradient-to-br from-sky-50/70 to-blue-50/40 border border-sky-200/80 p-6 rounded-2xl shadow-xs space-y-4 relative overflow-hidden">
                  
                  {/* AI Sparks Header Banner */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-sky-100 pb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-9 h-9 rounded-xl bg-[#0091ca]/10 border border-[#0091ca]/20 flex items-center justify-center shadow-xs">
                        <Sparkles className="w-5 h-5 text-[#0091ca] animate-pulse" />
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <h3 className="font-bold text-sm text-[#0c4a6e]">Oslo AI Content Engine</h3>
                          <span className="px-2 py-0.5 bg-sky-100 text-sky-700 font-bold text-[9px] rounded-full uppercase tracking-wider">
                            Domain-Grounded
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Synthesizes Career Cluster, Sub-Cluster, Occupation, and Host Context into a relevant role breakdown.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleTriggerDraft}
                      disabled={draftingProgress}
                      className="px-5 py-2.5 bg-[#0091ca] hover:bg-[#007ba6] disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-black tracking-wider rounded-xl shadow-xs flex items-center justify-center space-x-2 cursor-pointer transition-all border border-[#0091ca] shrink-0"
                    >
                      {draftingProgress ? (
                        <>
                          <RefreshCw className="w-4 h-4 text-white animate-spin" />
                          <span>SYNTHESIZING...</span>
                        </>
                      ) : (overview || responsibilitiesText || qualificationsText) ? (
                        <>
                          <RefreshCw className="w-4 h-4 text-white" />
                          <span>REGENERATE DESCRIPTION</span>
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 text-white" />
                          <span>AUTO-DRAFT DESCRIPTION</span>
                        </>
                      )}
                    </button>
                  </div>

                  {/* Active Synthesizer Grounding Chips */}
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-mono">
                      Grounded In:
                    </span>
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-white border border-sky-200 text-sky-900 shadow-2xs">
                      <Layers className="w-3 h-3 mr-1 text-sky-600" />
                      Cluster: {careerCluster || 'General'}
                    </span>
                    {functionalFocus && functionalFocus !== 'General' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-white border border-sky-200 text-sky-900 shadow-2xs">
                        <Tag className="w-3 h-3 mr-1 text-sky-600" />
                        Sub-Cluster: {functionalFocus}
                      </span>
                    )}
                    {occupationSelection && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-white border border-sky-200 text-sky-900 shadow-2xs">
                        <Briefcase className="w-3 h-3 mr-1 text-sky-600" />
                        Occupation: {occupationSelection}
                      </span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold bg-white border border-sky-200 text-sky-900 shadow-2xs">
                      <GraduationCap className="w-3 h-3 mr-1 text-sky-600" />
                      Level: {targetLevel || 'High School'}
                    </span>
                  </div>

                  {/* KEYWORDS */}
                  <div className="space-y-1 pt-1">
                    <label className="block text-[10px] font-bold text-[#0c4a6e] uppercase tracking-wider">
                      CONTEXTUAL KEYWORDS & SKILLS (OPTIONAL)
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., customer service, diagnostics, CAD software, social media, safety compliance"
                      value={keywords}
                      onChange={(e) => setKeywords(e.target.value)}
                      className="w-full bg-white border border-[#bae6fd] outline-none rounded-lg p-2.5 focus:ring-1 focus:ring-sky-500 text-slate-800 text-xs font-medium placeholder:text-slate-400"
                    />
                  </div>

                  {draftingProgress && (
                    <div className="p-3 text-[11px] text-sky-900 bg-sky-100/70 rounded-xl flex items-center space-x-2.5 font-medium border border-sky-200 animate-pulse">
                      <span className="w-2.5 h-2.5 rounded-full bg-sky-500 animate-ping shrink-0" />
                      <span>{draftingStatusMsg}</span>
                    </div>
                  )}
                </div>

                {/* 7. Narrative & Content Box containing the three Textareas & Toolbar */}
                <div className="space-y-6 bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                    <div>
                      <h2 className="text-base font-bold text-slate-850">Narrative & Content</h2>
                      <p className="text-[10px] text-slate-400 font-mono tracking-wider uppercase mt-0.5">
                        ROLE DETAILS, QUALIFICATIONS, AND LEARNING OBJECTIVES
                      </p>
                    </div>
                    {(overview || responsibilitiesText || qualificationsText) && (
                      <button
                        type="button"
                        onClick={handleTriggerDraft}
                        disabled={draftingProgress}
                        className="text-[11px] font-bold text-[#0091ca] hover:text-[#007ba6] flex items-center space-x-1 cursor-pointer transition-colors"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${draftingProgress ? 'animate-spin' : ''}`} />
                        <span>Regenerate Content</span>
                      </button>
                    )}
                  </div>

                  {/* POSITION OVERVIEW */}
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">POSITION OVERVIEW</label>
                    <RichTextEditor
                      value={overview}
                      onChange={setOverview}
                      placeholder="Provide a general career rotation placement overview..."
                      minHeight="min-h-20"
                      maxHeight="max-h-52"
                    />
                  </div>

                  {/* KEY RESPONSIBILITIES */}
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">KEY RESPONSIBILITIES</label>
                    <RichTextEditor
                      value={responsibilitiesText}
                      onChange={setResponsibilitiesText}
                      placeholder="Outline main duties, mentorship, and deliverables..."
                      minHeight="min-h-20"
                      maxHeight="max-h-52"
                    />
                  </div>

                  {/* QUALIFICATIONS */}
                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">QUALIFICATIONS</label>
                    <RichTextEditor
                      value={qualificationsText}
                      onChange={setQualificationsText}
                      placeholder="Detail minimum grade point averages, background checks, or pathways requirements..."
                      minHeight="min-h-20"
                      maxHeight="max-h-52"
                    />
                  </div>

                  {/* LEARNING OBJECTIVES */}
                  <div className="space-y-1.5 pt-2 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                      <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        Learning Objective Statement
                      </label>
                      <button
                        type="button"
                        onClick={handleGenerateLearningObjective}
                        disabled={generatingObjective}
                        className="flex items-center space-x-1 px-2.5 py-1 bg-sky-50 hover:bg-sky-100 text-sky-700 font-bold rounded-lg border border-sky-200 text-[10px] transition-all cursor-pointer shadow-2xs"
                      >
                        {generatingObjective ? (
                          <>
                            <RefreshCw className="w-3 h-3 text-sky-600 animate-spin" />
                            <span>Drafting Objective...</span>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3 h-3 text-sky-600" />
                            <span>{learningObjective ? 'Regenerate Objective' : 'Generate with Oslo AI'}</span>
                          </>
                        )}
                      </button>
                    </div>
                    <RichTextEditor
                      value={learningObjective}
                      onChange={setLearningObjective}
                      placeholder="Generate or write a short 2-3 sentence learning objective statement..."
                      minHeight="min-h-20"
                      maxHeight="max-h-36"
                    />
                  </div>
                </div>

                {/* 8. Application Questionnaire */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200/80 shadow-xs space-y-4">
                  <div>
                    <h2 className="text-base font-bold text-slate-850">Application Questionnaire</h2>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-mono">
                      ADD SPECIFIC QUESTIONS FOR CANDIDATES TO ANSWER DURING THEIR APPLICATION.
                    </p>
                  </div>

                  <div className="space-y-4 pt-1">
                    {questions.map((q, idx) => (
                      <div key={q.id} className="space-y-1.5" id={`question-field-${q.id}`}>
                        <div className="flex items-center justify-between">
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-widest font-mono">
                            QUESTION {idx + 1}
                          </label>
                          {questions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => handleRemoveQuestion(q.id)}
                              className="text-rose-500 hover:text-rose-700 font-bold text-[10.5px] uppercase cursor-pointer"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          required
                          placeholder="e.g., Why are you interested in this role?"
                          value={q.text}
                          onChange={(e) => handleQuestionTextChange(q.id, e.target.value)}
                          className="w-full bg-[#f4f6f8] border border-slate-200 outline-none rounded-lg p-3.5 focus:ring-1 focus:ring-[#0094c8] text-slate-850 text-xs font-medium placeholder:text-slate-405"
                        />
                      </div>
                    ))}

                    <div className="pt-2">
                      <button
                        type="button"
                        onClick={handleAddQuestion}
                        className="px-4 py-2.5 bg-[#f4f6f8] hover:bg-slate-200/80 text-slate-700 font-bold rounded-lg border border-slate-250 flex items-center space-x-1.5 cursor-pointer text-[10px] uppercase tracking-wider transition-all"
                      >
                        <Plus className="w-4 h-4 text-slate-600" />
                        <span>Add Supplemental Question</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Submit and Cancel Buttons */}
                <div className="flex items-center justify-end space-x-4 pt-4 border-t border-slate-200">
                  {editingOppId && (
                    <button
                      type="button"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="px-6 py-3 bg-rose-50 border border-rose-200 hover:bg-rose-100 text-rose-700 font-bold text-xs rounded-xl flex items-center space-x-1.5 cursor-pointer mr-auto transition-all shadow-xs"
                    >
                      <Trash2 className="w-4 h-4 text-rose-600 animate-pulse" />
                      <span>Delete Posting</span>
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => setActiveTab('all')}
                    className="px-6 py-3 bg-white border border-slate-200 hover:bg-slate-55 transition-all font-bold text-slate-500 text-xs rounded-xl cursor-pointer"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3 bg-[#0f2942] hover:bg-[#1e293b] active:scale-95 transition-all text-white font-extrabold uppercase tracking-widest rounded-xl shadow-md flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-xs"
                    id="btn-publish-posting"
                  >
                    {isSubmitting ? (
                      <span className="animate-pulse">Broadcasting Configuration...</span>
                    ) : (
                      <>
                        <span>Publish Posting</span>
                        <CheckCircle className="w-4 h-4 text-white" />
                      </>
                    )}
                  </button>
                </div>

              </form>
            )}
          </motion.div>
        )}

      </div>

      {/* Slide / Modal quick apply for candidates */}
      {selectedOppForApply && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 animate-fade-in text-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-slate-200 animate-in zoom-in-95 flex flex-col">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 p-5 md:p-6 bg-slate-50/50 rounded-t-2xl">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-white border border-slate-200/60 rounded-xl p-1 flex items-center justify-center shadow-xs overflow-hidden shrink-0">
                  {(() => {
                    const orgProfile = (users || []).find(u => u.uid === selectedOppForApply.organizationId);
                    const orgLogo = orgProfile?.avatar || selectedOppForApply.externalLogoUrl;
                    return getOrganizationLogo(
                      selectedOppForApply.externalOrganizationName || selectedOppForApply.organizationName,
                      orgLogo,
                      selectedOppForApply.organizationId,
                      selectedOppForApply.careerCluster || selectedOppForApply.industry
                    );
                  })()}
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-900 text-sm md:text-base leading-snug">
                    {selectedOppForApply.title}
                  </h4>
                  <p className="text-[10px] text-slate-500 font-semibold tracking-wide uppercase">
                    {selectedOppForApply.externalOrganizationName || selectedOppForApply.organizationName}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedOppForApply(null)} 
                className="text-slate-400 font-extrabold hover:text-slate-600 cursor-pointer h-8 w-8 hover:bg-slate-100 rounded-lg flex items-center justify-center transition-all text-sm"
              >
                ✕
              </button>
            </div>

            {/* Modal Body: Fast Facts & Questions Form */}
            <div className="p-6 md:p-8 overflow-y-auto space-y-6">
              
              {/* Key Insights Panel */}
              <div className="bg-slate-50 border border-slate-200/80 rounded-2xl p-5 md:p-6 space-y-4">
                <h5 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider font-mono pb-2 border-b border-slate-200 flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span>Rotation Fast Facts</span>
                </h5>

                <div className="space-y-3.5 text-[11px] font-medium text-slate-600">
                  <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                    <span className="text-slate-400">Compensation:</span>
                    <span className="font-bold text-[#0f2942] uppercase text-right leading-tight">
                      {selectedOppForApply.compensation || "Paid"} 
                      {selectedOppForApply.hourlyRate && selectedOppForApply.hourlyRate !== "0" && ` ($${selectedOppForApply.hourlyRate}/hr / ${selectedOppForApply.paymentStructure || 'Stipend'})`}
                    </span>
                  </div>

                  <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                    <span className="text-slate-400">Required Credits:</span>
                    <span className="font-bold text-slate-800">{selectedOppForApply.totalHours || selectedOppForApply.hoursRequired} Hours</span>
                  </div>

                  <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                    <span className="text-slate-400">Application Deadline:</span>
                    <span className="font-bold text-rose-700">{selectedOppForApply.applicationDeadline ? formatOpportunityDate(selectedOppForApply.applicationDeadline) : 'Ongoing Review'}</span>
                  </div>

                  <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                    <span className="text-slate-400">Operating Semester:</span>
                    <span className="font-bold text-slate-800">{selectedOppForApply.semester || selectedTerm || "Fall 2026"}</span>
                  </div>

                  <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                    <span className="text-slate-400">Location / Venue:</span>
                    <span className="font-bold text-slate-800 text-right max-w-[200px] leading-tight flex items-center gap-1 justify-end">
                      <MapPin className="w-3 h-3 text-slate-400 shrink-0" />
                      <span>{selectedOppForApply.address || selectedOppForApply.location}</span>
                    </span>
                  </div>

                  <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                    <span className="text-slate-400">Supervisor:</span>
                    <span className="font-bold text-slate-800 text-right leading-tight">
                      {selectedOppForApply.reportsTo || "Cam O'Sullivan"}
                      <span className="block text-[9px] text-slate-400 font-normal">{selectedOppForApply.supervisorTitle || "WBL Coordinator"}</span>
                    </span>
                  </div>

                  {selectedOppForApply.externalContactEmail && (
                    <div className="flex items-start justify-between border-b border-slate-200/40 pb-2">
                      <span className="text-slate-400">Contact Email:</span>
                      <span className="font-bold text-[#0094c8] lowercase font-mono">{selectedOppForApply.externalContactEmail}</span>
                    </div>
                  )}

                  <div className="flex items-start justify-between pb-1">
                    <span className="text-slate-400">Openings Available:</span>
                    <span className="font-black text-emerald-800 bg-emerald-50 border border-emerald-120 font-mono px-2 py-0.5 rounded-sm">
                      {selectedOppForApply.numOpenings || 1} Positions
                    </span>
                  </div>
                </div>
              </div>

              {/* Apply Action / Role Check */}
              <div className="bg-slate-50 rounded-2xl p-5 md:p-6 border border-slate-200/80 space-y-4">
                {(() => {
                  const hasApplied = selectedOppForApply && myApplications.some(a => a.opportunityId === selectedOppForApply.id);
                  const application = hasApplied ? myApplications.find(a => a.opportunityId === selectedOppForApply.id) : null;

                  if (hasApplied) {
                    return (
                      <div className="bg-white border border-emerald-200 rounded-xl p-5 text-center space-y-3">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
                          <CheckCircle className="w-7 h-7 text-emerald-600" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight">Application Submitted</h4>
                        <p className="text-[11px] text-slate-500 leading-relaxed max-w-[240px] mx-auto">
                          Your application for this placement was successfully received on <span className="font-bold text-slate-700">{application?.submittedAt ? formatOpportunityDate(application.submittedAt) : 'Recent'}</span>.
                        </p>
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold transition-all cursor-pointer text-[10px] shadow-sm"
                          >
                            Close Status View
                          </button>
                        </div>
                      </div>
                    );
                  }

                  const eligibility = selectedOppForApply ? getStudentEligibility(selectedOppForApply, activeUser, dbDistricts, dbSchools) : { eligible: true, reasons: [] };
                  if (!eligibility.eligible) {
                    return (
                      <div className="bg-white border border-amber-200 rounded-xl p-5 text-center space-y-3">
                        <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                          <ShieldAlert className="w-7 h-7 text-amber-600 animate-bounce" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-800 uppercase tracking-tight">Eligibility Requirements Not Met</h4>
                        <ul className="text-[11px] text-slate-500 leading-relaxed max-w-md mx-auto list-disc pl-5 text-left space-y-1">
                          {eligibility.reasons.map((reason, idx) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="w-full px-4 py-2 bg-slate-900 text-white rounded-xl font-bold transition-all cursor-pointer text-[10px]"
                          >
                            Close Details
                          </button>
                        </div>
                      </div>
                    );
                  }

                  if (activeUser?.role !== 'student') {
                    return (
                      <div className="bg-white border border-slate-200/50 rounded-xl p-4 text-center space-y-2">
                        <ShieldAlert className="w-6 h-6 mx-auto text-amber-500 animate-bounce" />
                        <p className="text-[11px] font-semibold text-slate-600 leading-normal">
                          Only Student accounts can apply for placements.
                        </p>
                        <p className="text-[10px] text-slate-400 flex flex-wrap justify-center gap-1">
                          Your role is classified as: <span className="font-extrabold text-slate-600 uppercase font-mono">{activeUser?.role}</span>
                        </p>
                        <div className="pt-2">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold transition-all cursor-pointer text-[10px]"
                          >
                            Close Details View
                          </button>
                        </div>
                      </div>
                    );
                  }

                  if (!activeUser?.ferpaConsent?.granted) {
                    return (
                      <div className="bg-white border border-amber-200 rounded-2xl p-5 space-y-4 shadow-sm">
                        <div className="text-center space-y-2">
                          <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mx-auto">
                            <ShieldAlert className="w-7 h-7 text-amber-600" />
                          </div>
                          <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">FERPA Consent Required</h4>
                          <p className="text-xs text-slate-500 leading-relaxed max-w-sm mx-auto font-medium">
                            You cannot apply for opportunities until a parent or legal guardian has provided FERPA consent (or yourself if you are 18+).
                          </p>
                        </div>

                        <div className="pt-1">
                          <FerpaConsentReminderCard 
                            compact={true}
                            title="Send Parent Consent Email"
                            subtitle="Send an instant email notification to your parent or legal guardian to grant authorization."
                            buttonText="Send Email to Parent"
                          />
                        </div>

                        <div className="pt-2 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="w-full px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-bold transition-all cursor-pointer text-xs uppercase tracking-wider"
                          >
                            Close Details View
                          </button>
                        </div>
                      </div>
                    );
                  }

                  const isDenied = !!(activeUser?.advisorDenied || activeUser?.verificationStatus === 'denied');
                  if (activeUser?.role === 'student' && isDenied) {
                    return (
                      <div className="bg-white border border-rose-200 rounded-2xl p-5 text-center space-y-3.5 shadow-sm">
                        <div className="w-12 h-12 bg-rose-50 border border-rose-100 rounded-full flex items-center justify-center mx-auto">
                          <XCircle className="w-6 h-6 text-rose-600" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-black text-rose-800 uppercase tracking-tight">Student Verification Denied</h4>
                          <p className="text-[11px] text-slate-500 leading-relaxed max-w-[280px] mx-auto font-medium">
                            Your account verification was marked as denied or invalid by your school advisor or administrator. You cannot submit placement applications. Please contact your school counselor or advisor to review your account status.
                          </p>
                        </div>

                        <div className="pt-1 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold transition-all cursor-pointer text-[10px]"
                          >
                            Close Details View
                          </button>
                        </div>
                      </div>
                    );
                  }

                  const isVerified = !!(activeUser?.advisorVerified || activeUser?.verified);
                  if (activeUser?.role === 'student' && !isVerified) {
                    return (
                      <div className="bg-white border border-blue-200 rounded-2xl p-5 text-center space-y-3.5 shadow-sm">
                        <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-full flex items-center justify-center mx-auto">
                          <UserCheck className="w-6 h-6 text-[#0094c8]" />
                        </div>
                        <div className="space-y-1">
                          <h4 className="text-sm font-black text-slate-800 uppercase tracking-tight">Advisor Verification Required</h4>
                          <p className="text-[11px] text-slate-500 leading-relaxed max-w-[280px] mx-auto font-medium">
                            Your student profile must be verified by your school advisor, regional administrator, or superadmin before you can submit applications for placement opportunities.
                          </p>
                        </div>

                        {sendingReminder ? (
                          <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center gap-2 text-slate-600 font-bold text-xs">
                            <Loader2 className="w-4 h-4 animate-spin text-[#0094c8]" />
                            <span>Sending reminder to school advisor...</span>
                          </div>
                        ) : reminderSent ? (
                          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-center gap-2 text-emerald-700 font-bold text-xs">
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                            <span>Reminder Sent to Advisor!</span>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendAdvisorReminder}
                            className="w-full px-4 py-2.5 bg-[#0094c8] hover:bg-[#007ba6] text-white rounded-xl font-bold transition-all cursor-pointer text-xs shadow-xs flex items-center justify-center gap-2"
                          >
                            <Mail className="w-4 h-4" />
                            <span>Send Verification Reminder to Advisor</span>
                          </button>
                        )}

                        <div className="pt-1 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl font-bold transition-all cursor-pointer text-[10px]"
                          >
                            Close Details View
                          </button>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <>
                      <h5 className="font-bold text-slate-800 text-[11px] uppercase tracking-wider font-mono pb-2 border-b border-slate-200 flex items-center gap-1.5">
                        <Send className="w-4 h-4 text-custom-primary" />
                        <span>Apply for Academic Placement</span>
                      </h5>

                      <form onSubmit={handleApplySubmit} className="space-y-4 text-xs">
                        <p className="text-slate-500 leading-normal text-[11px] italic">
                          Please provide your answers to the additional selection questions below. Your answers will be submitted directly as part of your application.
                        </p>

                        {(() => {
                          const questionsToAnswer = selectedOppForApply.customQuestions && selectedOppForApply.customQuestions.length > 0
                            ? selectedOppForApply.customQuestions
                            : [];

                          return questionsToAnswer.map((q, idx) => (
                            <div key={idx} className="space-y-1.5">
                              <label className="block text-[10.5px] font-bold text-slate-700 leading-normal">
                                Q{idx + 1}: {q}
                              </label>
                              <textarea
                                required
                                placeholder="Type your response here..."
                                value={questionAnswers[idx] || ''}
                                onChange={(e) => setQuestionAnswers(prev => ({ ...prev, [idx]: e.target.value }))}
                                className="w-full bg-white border border-slate-250 outline-none rounded-lg p-2.5 h-24 resize-none focus:ring-1 focus:ring-custom-primary text-slate-800 leading-relaxed font-medium text-[11px]"
                              />
                            </div>
                          ));
                        })()}

                        <div className="flex gap-2 justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => setSelectedOppForApply(null)}
                            className="px-4 py-2 border border-slate-200 bg-white rounded-xl font-bold text-slate-500 hover:bg-slate-50 transition-all cursor-pointer text-[10px]"
                          >
                            Cancel
                          </button>
                          
                          <button
                            type="submit"
                            className="px-5 py-2 bg-slate-900 border border-slate-850 hover:bg-slate-800 active:scale-95 text-white rounded-xl font-bold cursor-pointer transition-all text-[10px]"
                          >
                            Submit Placement application
                          </button>
                        </div>
                      </form>
                    </>
                  );
                })()}
              </div>

            </div>
            
          </div>
        </div>
      )}

      {/* Advisor Recommendation Modal */}
      {recommendModalOpen && selectedOpp && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 animate-fade-in text-xs">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-slate-200 animate-in zoom-in-95 flex flex-col overflow-hidden">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-slate-100 p-5 bg-slate-50/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-[#0f2d4a]/10 rounded-xl flex items-center justify-center text-[#0f2d4a]">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <div>
                  <h4 className="font-extrabold text-slate-900 text-base leading-snug">
                    Recommend Placement
                  </h4>
                  <p className="text-[10px] text-slate-500 font-semibold tracking-wide uppercase">
                    Promote "{selectedOpp.title}" to students
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setRecommendModalOpen(false)} 
                className="text-slate-400 font-extrabold hover:text-slate-600 cursor-pointer h-8 w-8 hover:bg-slate-100 rounded-lg flex items-center justify-center transition-all text-sm"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {recommendationProcessing ? (
                <div className="py-12 flex flex-col items-center justify-center space-y-6 text-center">
                  {recommendationComplete ? (
                    <>
                      <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center animate-in zoom-in duration-500">
                        <CheckCircle className="w-10 h-10" />
                      </div>
                      <div>
                        <h3 className="text-xl font-black text-slate-900">Recommendation Dispatched!</h3>
                        <p className="text-sm text-slate-500 mt-2 max-w-[280px] mx-auto leading-relaxed">
                          Alerts have been sent via email and portal messages to the selected candidates.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="w-full max-w-[240px] h-2.5 bg-slate-100 rounded-full overflow-hidden border border-slate-200 shadow-inner">
                        <motion.div 
                          className="h-full bg-[#0091ca]"
                          initial={{ width: "0%" }}
                          animate={{ width: `${recommendationProgress}%` }}
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="text-[#0091ca] font-black uppercase tracking-widest text-[10px] animate-pulse">
                          Transmitting Candidate Briefs...
                        </p>
                        <p className="text-[11px] text-slate-400 font-medium">
                          Synchronizing with student recruitment pipelines
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    {(() => {
                      const eligibleCount = selectedOpp
                        ? advisorAdvisees.filter((s: any) => getStudentEligibility(selectedOpp, s, dbDistricts, dbSchools).eligible).length
                        : advisorAdvisees.length;
                      return (
                        <label className="text-[11px] font-black text-slate-500 uppercase tracking-wider flex items-center justify-between">
                          <span>Select Candidates ({eligibleCount} eligible of {advisorAdvisees.length})</span>
                          <span className="text-[10px] font-medium text-[#0091ca] normal-case">
                            {selectedStudentsForRecommend.length} selected
                          </span>
                        </label>
                      );
                    })()}
                    
                    {advisorAdvisees.length === 0 ? (
                      <div className="p-8 border-2 border-dashed border-slate-100 rounded-2xl text-center space-y-2">
                        <Users className="w-8 h-8 text-slate-300 mx-auto" />
                        <p className="text-xs text-slate-500 font-medium leading-relaxed">
                          No students currently found in your serve list.
                        </p>
                      </div>
                    ) : (
                      <div className="max-h-[300px] overflow-y-auto border border-slate-100 rounded-2xl divide-y divide-slate-50 shadow-inner bg-slate-50/30">
                        {sortStudentsByLastName(advisorAdvisees as any[], (s: any) => s.name, (s: any) => s).map((student: any) => {
                          const eligibility = selectedOpp 
                            ? getStudentEligibility(selectedOpp, student, dbDistricts, dbSchools)
                            : { eligible: true, reasons: [] };
                          const canApply = eligibility.eligible;
                          const isSelected = selectedStudentsForRecommend.includes(student.uid);

                          return (
                            <div 
                              key={student.uid}
                              onClick={() => {
                                if (!canApply) return;
                                setSelectedStudentsForRecommend(prev => 
                                  prev.includes(student.uid) 
                                    ? prev.filter(id => id !== student.uid) 
                                    : [...prev, student.uid]
                                );
                              }}
                              className={`p-4 flex items-center justify-between transition-all ${
                                !canApply
                                  ? 'bg-slate-50/80 opacity-60 cursor-not-allowed select-none'
                                  : 'cursor-pointer hover:bg-white group ' + (isSelected ? 'bg-white' : '')
                              }`}
                              title={!canApply ? eligibility.reasons.join('; ') : undefined}
                            >
                              <div className="flex items-center gap-3">
                                {canApply ? (
                                  <div className={`w-5 h-5 rounded-md border flex items-center justify-center transition-all shadow-sm ${
                                    isSelected 
                                      ? 'bg-[#0091ca] border-[#0091ca] text-white' 
                                      : 'bg-white border-slate-200 text-transparent group-hover:border-slate-300'
                                  }`}>
                                    <Check className="w-3.5 h-3.5" />
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 rounded-md border border-slate-200 bg-slate-100 flex items-center justify-center text-slate-400">
                                    <Lock className="w-3 h-3 text-slate-400" />
                                  </div>
                                )}
                                <StudentAvatar 
                                  studentName={formatLastNameFirst(student.name, student)} 
                                  studentId={student.uid} 
                                  imageUrl={student.avatar} 
                                  className="w-8 h-8 rounded-lg shadow-sm"
                                />
                                <div className="flex flex-col">
                                  <span className="font-bold text-slate-800 text-xs">{formatLastNameFirst(student.name, student)}</span>
                                  <span className="text-[10px] text-slate-400 font-mono">{student.email}</span>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                {canApply ? (
                                  <div className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded uppercase tracking-tighter">
                                    {student.school || 'Unspecified'}
                                  </div>
                                ) : (
                                  <>
                                    <span className="text-[9px] font-black text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded uppercase tracking-wider flex items-center gap-1">
                                      <XCircle className="w-3 h-3 text-rose-600" />
                                      Cannot Apply
                                    </span>
                                    <span className="text-[9px] text-slate-400 max-w-[160px] truncate text-right">
                                      {eligibility.reasons[0] || 'Restricted'}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <div className="pt-4 flex items-center gap-3">
                    <button
                      onClick={() => setRecommendModalOpen(false)}
                      className="flex-1 px-6 py-4 bg-slate-100 hover:bg-slate-200 text-slate-600 font-black text-[11px] uppercase tracking-widest rounded-xl transition-all cursor-pointer"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleRecommendPlacement}
                      disabled={selectedStudentsForRecommend.length === 0}
                      className="flex-[2] px-6 py-4 bg-[#0f2d4a] hover:bg-[#1e293b] disabled:opacity-30 disabled:grayscale text-white font-black text-[11px] uppercase tracking-widest rounded-xl transition-all shadow-lg shadow-[#0f2d4a]/20 flex items-center justify-center gap-2 cursor-pointer"
                    >
                      Send Recommendations
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Custom Confirm Delete Modal to avoid browser alert/confirm iframe restrictions */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-150 space-y-4 text-left"
            >
              <div className="flex items-center space-x-3 text-rose-600">
                <ShieldAlert className="w-6 h-6 shrink-0 text-rose-500 animate-bounce" />
                <h3 className="text-sm font-black text-slate-900 uppercase tracking-wider">Archive / Delete Posting</h3>
              </div>
              
              <div className="text-slate-600 text-xs space-y-2.5 leading-relaxed font-medium">
                <p>Are you sure you want to delete/archive this posting?</p>
                <p className="bg-slate-50 p-3 rounded-lg border border-slate-150 text-slate-500 italic text-[11px]">
                  Because students may have already applied, and advisors may have recommended this, it will not be permanently deleted. Instead, it will be safely archived (soft-deleted) in order to keep past applications, placements, and historical statistics intact while immediately removing this posting from student search results.
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      if (editingOppId) {
                        await updateOpportunity(editingOppId, { status: 'archived' });
                        setShowDeleteConfirm(false);
                        setEditingOppId(null);
                        resetFormState();
                        setActiveTab('all');
                        showToast({
                          title: 'Opportunity Archived',
                          message: 'The opportunity has been safely archived.',
                          type: 'info',
                        });
                      }
                    } catch (err: any) {
                      showAlert({
                        title: 'Delete Failed',
                        message: 'Failed to delete/archive opportunity: ' + err.message,
                        type: 'error',
                      });
                    }
                  }}
                  className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all cursor-pointer shadow-sm"
                >
                  Confirm Delete
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Crop Modal Dialogue Overlay */}
      {isCropOpen && cropImageSrc && (
        <ImageCropper
          imageSrc={cropImageSrc}
          onApply={handleCropApply}
          onClose={() => {
            setIsCropOpen(false);
            setCropImageSrc(null);
          }}
        />
      )}

      {isOppCropOpen && oppCropImageSrc && (
        <ImageCropper
          imageSrc={oppCropImageSrc}
          aspect={16 / 9}
          onApply={handleOppCropApply}
          onClose={() => {
            setIsOppCropOpen(false);
            setOppCropImageSrc(null);
          }}
        />
      )}

    </div>
  );
};
